import maplibregl from 'maplibre-gl'
import { defineStore } from 'pinia'

import { apiService } from '@/services/apiService'
import CONFIG from '@/services/configService'
import { connectivityService } from '@/services/connectivityService'
import { parseFIT } from '@/services/fitService'
import { formatByteSize } from '@/services/formattingService'
import { parseGPXFromArrayBuffer } from '@/services/gpxService'
import { generateId } from '@/services/idService'
// Workspaces and tours are now stored in memory only - no IndexedDB persistence
import { buildLineStringFromSegments, parseLineString } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { throwIfOfflineReadOnly } from '@/services/offlineModificationGuardService'
import { poiSnapshot } from '@/services/poiService'
import { importSuccessMessage } from '@/services/syncCopy'
import { syncService } from '@/services/syncService'
import { syncDialogVisible } from '@/services/syncUiState'
import { showError, showSuccess } from '@/services/toastService'
import { isKnownTrackTypeId } from '@/services/tourTypeService'
import {
  applyPersistedWorkspaceTourVisibility,
  persistWorkspaceTourVisibility,
} from '@/services/tourVisibilityLocalStorageService'
import { TrackColorService } from '@/services/trackColorService'
import {
  calculateTrackDistance,
  getTrackElevationGain,
  getTrackTimeElapsed,
  getTrackTimeMoving,
} from '@/services/trackMetricsCalculationService'
import { useSearchWorkspaceStore } from '@/stores/searchWorkspaceStore'
import { EventType } from '@/types/event'
import type { EventChange } from '@/types/event'
import type { WorkspacePOI } from '@/types/poi'
import type { Tour, Track, Waypoint, Segment, RoutePoint, TourSource } from '@/types/tour'
import type { Workspace as WorkspaceBase } from '@/types/workspace'
import {
  ensureSegmentClientIds,
  ensureTrackSegmentClientIds,
} from '@/utils/ensureTrackSegmentClientIds'
import { hydrateApiSegments } from '@/utils/hydrateApiSegments'
import { ensureImportedRoutePoints } from '@/utils/importRoutePointFallback'
import { requireItemEditable } from '@/utils/permissionDialog'
import { refreshTourTrackStats, refreshTrackMetrics } from '@/utils/refreshTrackMetrics'
import { findNearestTrackClientId } from '@/utils/routePointGrouping'
import {
  getRoutePointInsertOrderPlan,
  getRoutePointMultiDeletePlan,
  getRoutePointMultiInsertOrderPlan,
  ROUTE_POINT_ORDER_STEP,
  getSparseTrackAppendOrder,
  getSparseTrackReorderPlan,
  getSparseTrackSeedOrders,
} from '@/utils/routePointOrder'
import { sortSegmentOrderShiftsForEmission } from '@/utils/segmentOrder'
import { getBestTrackLineString } from '@/utils/trackGeometry'
import { requireTrackClientId } from '@/utils/trackRoutePoints'
import { truncateToMaxLength } from '@/utils/validation'

import { useEventStore } from './eventStore'
import { usePhotoStore } from './photoStore'
import { useUiStore } from './uiStore'

/**
 * Thrown when a direct (non-event-sourced) API operation is attempted on a collection that the
 * server does not know about yet (its WORKSPACE_CREATED has not synced — e.g. offline, or the
 * user acted immediately after creating it). Callers surface a friendly message instead of a raw
 * 404. See docs/concepts/sync-ordering-edge-cases.md (Group 2).
 */
export class WorkspaceNotSyncedError extends Error {
  constructor(
    message = 'This collection is still syncing. Try again once it has finished syncing.',
  ) {
    super(message)
    this.name = 'WorkspaceNotSyncedError'
  }
}

interface Workspace extends WorkspaceBase {
  _error?: string | null
  _selectedTourId?: string | null
  /** Tour whose full detail is currently being fetched, for a loading spinner on the profile. */
  _loadingTourDetailId?: string | null
  _hoveredTourId?: string | null
  _activelyHoveredTourId?: string | null
  _lastAddedTourId?: string | null
  _isLoading?: boolean
  _createEventPushed: boolean
  _previousState?: Partial<WorkspaceBase>
  // User role and permission fields from API.
  // canEdit = may contribute content (owner/collaborator); canManage = owner-only administration.
  userRole?: string | null
  canEdit?: boolean | null
  canManage?: boolean | null
  canView?: boolean | null
  isBookmarked?: boolean
  ownerNames?: string[] | null
  // Cover photo resolved URLs (not persisted, populated from API response)
  coverPhotoThumbnailUrl?: string | null
  coverPhotoUrl?: string | null
}

interface WorkspaceState extends Workspace {
  tours: Tour[]
  pois: WorkspacePOI[]
}

const newWorkspaceDefaultName = 'My New Workspace'

interface TrackImportFailure {
  fileName: string
  reason: string
}

interface LoadTrackFilesResult {
  importedTourIds: string[]
  importedPOIIds: string[]
  failedImports: TrackImportFailure[]
}

export interface TrackRouteDraftCommitInput {
  mode: 'drawing' | 'edit'
  tourId: string
  trackClientId: string
  draftTrack: Track
  draftRoutePoints: RoutePoint[]
  baselineTrack?: Track | null
  baselineRoutePoints: RoutePoint[]
}

const pendingTourDetailLoads = new Map<string, Promise<Tour | null>>()

const clonePlain = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const routePointLocation = (routePoint: Pick<RoutePoint, 'lat' | 'lon'>) =>
  `SRID=4326;POINT(${routePoint.lon} ${routePoint.lat})`

const samePlainValue = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null)

const sameSegment = (left: Segment, right: Segment) =>
  samePlainValue(clonePlain(left), clonePlain(right))

const isPinnedWorkspaceState = (workspace: Pick<WorkspaceState, 'systemKey' | 'isSystem'>) =>
  workspace.isSystem === true && workspace.systemKey === 'pinned'

const buildEmptyTour = (
  tourId: string,
  input?: {
    name?: string
    description?: string
    type?: string
    sourceTourClientId?: string | null
    sourceTour?: TourSource | null
  },
  options?: { localOnlyDraft?: boolean; order?: number },
): Tour => {
  const tourName = truncateToMaxLength(input?.name ?? 'New Tour', 255, 'Tour name') ?? 'New Tour'
  const tourDescription =
    truncateToMaxLength(input?.description ?? '', 2048, 'Tour description') ?? ''

  return {
    clientId: tourId,
    name: tourName,
    description: tourDescription,
    type: input?.type,
    tracks: [],
    waypoints: [],
    routePoints: [],
    visible: true,
    active: false,
    order: options?.order ?? 0,
    distance: null,
    time_moving: null,
    time_elapsed: null,
    sourceFilename: null,
    canEdit: true,
    sourceTourClientId: input?.sourceTourClientId ?? input?.sourceTour?.clientId ?? null,
    sourceTour: input?.sourceTour ?? null,
    _previousState: undefined,
    _detailsLoaded: true,
    _localOnlyDraft: options?.localOnlyDraft === true,
  }
}

const materializeTrackVisibility = (tour: Tour) => {
  const inheritedVisibility = tour.visible !== false

  for (const track of tour.tracks) {
    track.visible ??= inheritedVisibility
  }
}

const updateTourVisibilityFromTracks = (tour: Tour) => {
  tour.visible = tour.tracks.some((track) => track.visible !== false)
}

const replaceTrackRoutePoints = (
  tour: Tour,
  trackClientId: string,
  finalRoutePoints: RoutePoint[],
) => {
  const firstTrackRoutePointIndex = tour.routePoints.findIndex(
    (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
  )
  const remainingRoutePoints = tour.routePoints.filter(
    (routePoint) => (routePoint.trackClientId ?? null) !== trackClientId,
  )
  const insertIndex =
    firstTrackRoutePointIndex >= 0
      ? Math.min(firstTrackRoutePointIndex, remainingRoutePoints.length)
      : remainingRoutePoints.length

  remainingRoutePoints.splice(insertIndex, 0, ...finalRoutePoints)
  tour.routePoints = remainingRoutePoints
}

type RoutePointOrderEmission = {
  clientId: string
  previousOrder: number
  nextOrder: number
  finalChanges: EventChange[]
}

const emitRoutePointOrderChanges = async (
  queueRoutePointUpdate: (clientId: string, changes: EventChange[]) => Promise<unknown>,
  updates: RoutePointOrderEmission[],
  currentOrders: number[],
  nextOrders: number[],
) => {
  if (updates.length === 0) return

  if (updates.length > 1) {
    const temporaryOrderBase =
      Math.max(-1, ...currentOrders, ...nextOrders) + ROUTE_POINT_ORDER_STEP

    for (const update of updates) {
      await queueRoutePointUpdate(update.clientId, [
        { field: 'order', value: update.previousOrder + temporaryOrderBase },
      ])
    }
  }

  for (const update of updates) {
    await queueRoutePointUpdate(update.clientId, update.finalChanges)
  }
}

const getRoutePointTrackClientId = (routePoint: Record<string, unknown>, context: string): string =>
  requireTrackClientId(
    (routePoint.trackClientId as string | undefined) ??
      (routePoint.track_client_id as string | undefined),
    context,
  )

function normalizeApiWaypoints(
  waypoints: Array<Record<string, unknown>> | undefined,
): Tour['waypoints'] {
  return (waypoints ?? []).map((waypoint) => {
    let timeValue: number | undefined = undefined
    if (waypoint.time) {
      const timeAsNumber = Number(waypoint.time)
      timeValue = isNaN(timeAsNumber) ? new Date(waypoint.time as string).getTime() : timeAsNumber
    }

    return {
      ...waypoint,
      clientId: (waypoint.clientId as string | undefined) || generateId(),
      description:
        (waypoint.description as string | undefined) ?? (waypoint.desc as string | undefined),
      time: timeValue,
      _previousState: undefined,
    }
  }) as Tour['waypoints']
}

function normalizeApiRoutePoints(
  routePoints: Array<Record<string, unknown>> | undefined,
): Tour['routePoints'] {
  return (routePoints ?? []).map((routePoint, index) => ({
    ...routePoint,
    clientId: (routePoint.clientId as string | undefined) || generateId(),
    description:
      (routePoint.description as string | undefined) ?? (routePoint.desc as string | undefined),
    order: (routePoint.order as number | undefined) ?? index,
    trackClientId: getRoutePointTrackClientId(routePoint, 'API route point'),
    _previousState: undefined,
  })) as unknown as Tour['routePoints']
}

function getPersistedTourType(
  explicitType: string | null | undefined,
  tracks: Array<Pick<Track, 'type'>>,
): string | undefined {
  if (isKnownTrackTypeId(explicitType)) {
    return explicitType
  }

  return tracks.map((track) => track.type).find(isKnownTrackTypeId) ?? undefined
}

function normalizeApiTour(
  tour: Record<string, unknown>,
  options: { existingTour?: Tour | null; detailsLoaded: boolean },
): Tour {
  const existingTour = options.existingTour ?? null
  const rawCollectionName =
    ((tour.collectionName as string | null | undefined) ??
      (tour.collection_name as string | null | undefined)) ||
    null
  const hasExplicitCustomName =
    Object.prototype.hasOwnProperty.call(tour, 'customName') ||
    Object.prototype.hasOwnProperty.call(tour, 'custom_name')
  const rawCustomName = hasExplicitCustomName
    ? (((tour.customName as string | null | undefined) ??
        (tour.custom_name as string | null | undefined) ??
        null) as string | null)
    : null
  const resolvedName = (tour.name as string) || existingTour?.name || 'Unnamed Tour'
  const resolvedCustomName = hasExplicitCustomName
    ? rawCustomName
    : (existingTour?.customName ?? null)
  const sourceTour =
    ((tour.sourceTour as TourSource | null | undefined) ?? existingTour?.sourceTour) || null
  const routingProfile = Object.prototype.hasOwnProperty.call(tour, 'routingProfile')
    ? ((tour.routingProfile as string | null | undefined) ?? undefined)
    : existingTour?.routingProfile

  return {
    ...tour,
    clientId: (tour.clientId as string) || existingTour?.clientId || generateId(),
    name: resolvedName,
    // Derive collectionName from the resolved customName so the two never diverge:
    // an explicit custom_name:null must reset collectionName to the global name rather
    // than keep a stale override. An explicit collectionName from the API still wins.
    collectionName: rawCollectionName ?? (resolvedCustomName?.trim() || resolvedName),
    customName: resolvedCustomName,
    description: (tour.description as string | undefined) ?? existingTour?.description ?? undefined,
    type: getPersistedTourType(
      tour.type as string | null | undefined,
      ((tour.tracks as Track[] | undefined) ?? existingTour?.tracks ?? []).map((track) => ({
        type: track.type,
      })),
    ),
    visible:
      (tour.visible as boolean | undefined) !== undefined
        ? (tour.visible as boolean)
        : (existingTour?.visible ?? true),
    active:
      (tour.active as boolean | undefined) !== undefined
        ? (tour.active as boolean)
        : (existingTour?.active ?? false),
    expanded:
      (tour.expanded as boolean | undefined) !== undefined
        ? (tour.expanded as boolean)
        : existingTour?.expanded,
    order: (tour.order as number | undefined) ?? existingTour?.order ?? 0,
    routingProfile,
    distance: (tour.distance as number | null | undefined) ?? existingTour?.distance ?? null,
    time_moving:
      (tour.time_moving as number | null | undefined) ?? existingTour?.time_moving ?? null,
    time_elapsed:
      (tour.time_elapsed as number | null | undefined) ?? existingTour?.time_elapsed ?? null,
    sourceFilename:
      (tour.sourceFilename as string | null | undefined) ?? existingTour?.sourceFilename ?? null,
    canDownloadOriginal:
      (tour.canDownloadOriginal as boolean | undefined) ??
      existingTour?.canDownloadOriginal ??
      false,
    canEdit: (tour.canEdit as boolean | undefined) ?? existingTour?.canEdit ?? true,
    isPinned: (tour.isPinned as boolean | undefined) ?? existingTour?.isPinned ?? false,
    sourceTourClientId:
      (tour.sourceTourClientId as string | null | undefined) ??
      sourceTour?.clientId ??
      existingTour?.sourceTourClientId ??
      null,
    sourceTour,
    tracks: ((tour.tracks as Track[] | undefined) ?? []).map((track) => {
      const existingTrack =
        existingTour?.tracks.find((candidate) => candidate.clientId === track.clientId) ?? null

      return {
        ...track,
        clientId: track.clientId || existingTrack?.clientId || generateId(),
        visible: track.visible !== undefined ? track.visible : (existingTrack?.visible ?? true),
        color: TrackColorService.ensureValidColor(track.color ?? existingTrack?.color),
        segments: hydrateApiSegments(track.segments),
        routingProfile: track.routingProfile ?? existingTrack?.routingProfile,
        pointsLineReduced5m: track.pointsLineReduced5m ?? existingTrack?.pointsLineReduced5m,
        pointsLineReduced500m: track.pointsLineReduced500m ?? existingTrack?.pointsLineReduced500m,
        metrics: track.metrics ?? existingTrack?.metrics,
        type: track.type ?? existingTrack?.type,
        startTime: track.startTime ?? existingTrack?.startTime,
        endTime: track.endTime ?? existingTrack?.endTime,
        _previousState: undefined,
      }
    }),
    waypoints: normalizeApiWaypoints(tour.waypoints as Array<Record<string, unknown>> | undefined),
    routePoints: normalizeApiRoutePoints(
      tour.routePoints as Array<Record<string, unknown>> | undefined,
    ),
    _previousState: existingTour?._previousState,
    _detailsLoaded: options.detailsLoaded,
    _waypointsVisible: existingTour?._waypointsVisible,
  }
}

const formatTrackImportFailureSummary = (
  failedImports: TrackImportFailure[],
  importedCount: number,
) => {
  if (failedImports.length === 0) return null

  if (failedImports.length === 1) {
    const failure = failedImports[0]
    const partialSuccessSuffix =
      importedCount > 0
        ? ` Imported ${importedCount} other file${importedCount === 1 ? '' : 's'}.`
        : ''

    return `Track import failed for "${failure.fileName}": ${failure.reason}.${partialSuccessSuffix}`
  }

  const listedFiles = failedImports
    .slice(0, 3)
    .map((failure) => `"${failure.fileName}"`)
    .join(', ')
  const remainingCount = failedImports.length - Math.min(failedImports.length, 3)
  const remainingSuffix = remainingCount > 0 ? ` and ${remainingCount} more` : ''
  const partialSuccessSuffix =
    importedCount > 0
      ? ` Imported ${importedCount} other file${importedCount === 1 ? '' : 's'}.`
      : ''

  return `${failedImports.length} track files failed to import: ${listedFiles}${remainingSuffix}.${partialSuccessSuffix}`
}

export const useWorkspaceStore = defineStore('workspace', {
  state: (): WorkspaceState => ({
    clientId: generateId(),
    name: newWorkspaceDefaultName,
    description: null,
    isPublic: false,
    systemKey: null,
    isSystem: false,
    markedForDeletion: false,
    deletionScheduledAt: null,
    coverPhotoClientId: null,
    coverPhotoThumbnailUrl: null,
    coverPhotoUrl: null,
    tours: [],
    pois: [],
    _isLoading: false,
    _error: null,
    _selectedTourId: null,
    _loadingTourDetailId: null,
    _hoveredTourId: null,
    _activelyHoveredTourId: null,
    _lastAddedTourId: null,
    _createEventPushed: false,
    _previousState: undefined,
    userRole: 'Owner',
    canEdit: true,
    canManage: true,
    canView: true,
    isBookmarked: false,
    ownerNames: null,
  }),

  getters: {
    allTours: (state) => state.tours.slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
    allWorkspacePOIs: (state) =>
      state.pois.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    visibleTours: (state) =>
      state.tours.filter((tour) => tour.visible).sort((a, b) => (a.order || 0) - (b.order || 0)),
    activeTours: (state) =>
      state.tours.filter((tour) => tour.active).sort((a, b) => (a.order || 0) - (b.order || 0)),
    selectedTour: (state) =>
      state.tours.find((tour) => tour.clientId === state._selectedTourId) || null,
    selectedTourId: (state) => state._selectedTourId,
    /** True while the currently selected tour's full detail (segments/elevation) is loading. */
    isSelectedTourDetailLoading: (state) =>
      state._loadingTourDetailId != null && state._loadingTourDetailId === state._selectedTourId,
    hoveredTourId: (state) => state._hoveredTourId ?? null,
    activelyHoveredTourId: (state) => state._activelyHoveredTourId ?? null,
    hasError: (state) => state._error !== null,

    /**
     * True when the current collection is known to the server: it was loaded from the server, or
     * its WORKSPACE_CREATED has synced (no longer queued). Direct API operations (share, change
     * visibility, soft-delete) 404 before this is true. `_createEventPushed` alone is insufficient
     * — it is also true while the create is merely *queued*. See sync-ordering-edge-cases.md (Group 2).
     */
    isPersistedToServer: (state): boolean => {
      if (!state.clientId || !state._createEventPushed) return false
      return !useEventStore().pendingWorkspaceCreateClientIds.has(state.clientId)
    },

    getTourById: (state) => (clientId: string) =>
      state.tours.find((tour) => tour.clientId === clientId),
    lastAddedTour: (state) =>
      state._lastAddedTourId
        ? state.tours.find((tour) => tour.clientId === state._lastAddedTourId) || null
        : null,

    // Computed file counts based on actual data
    tourFileCount: (state) => state.tours.length,
    photoFileCount: () => {
      const photoStore = usePhotoStore()
      return photoStore.allPhotos.length
    },

    // Computed total distance of all tours
    totalDistance: (state) => {
      let totalDistance = 0

      state.tours.forEach((tour) => {
        tour.tracks.forEach((track) => {
          totalDistance += calculateTrackDistance(track)
        })
      })

      return totalDistance
    },

    // Computed total elevation gain of all tours
    totalElevationGain: (state) => {
      let totalGain = 0

      state.tours.forEach((tour) => {
        tour.tracks.forEach((track) => {
          const gain = getTrackElevationGain(track)
          if (gain && gain > 0) {
            totalGain += gain
          }
        })
      })

      return totalGain
    },

    // Computed total time moving of all tours
    totalTimeMoving: (state) => {
      let totalTime: number | null = null

      state.tours.forEach((tour) => {
        // Use tour-level time if available, otherwise sum from tracks
        if (tour.time_moving !== null && tour.time_moving !== undefined) {
          totalTime = (totalTime || 0) + tour.time_moving
        } else {
          tour.tracks.forEach((track) => {
            const trackTime = getTrackTimeMoving(track)
            if (trackTime && trackTime > 0) {
              totalTime = (totalTime || 0) + trackTime
            }
          })
        }
      })

      return totalTime
    },

    // Computed total time elapsed of all tours
    totalTimeElapsed: (state) => {
      let totalTime: number | null = null

      state.tours.forEach((tour) => {
        // Use tour-level time if available, otherwise sum from tracks
        if (tour.time_elapsed !== null && tour.time_elapsed !== undefined) {
          totalTime = (totalTime || 0) + tour.time_elapsed
        } else {
          tour.tracks.forEach((track) => {
            const trackTime = getTrackTimeElapsed(track)
            if (trackTime && trackTime > 0) {
              totalTime = (totalTime || 0) + trackTime
            }
          })
        }
      })

      return totalTime
    },

    // Computed average speed of all tours
    totalAverageSpeed: (state) => {
      // Calculate total distance
      let totalDistance = 0
      state.tours.forEach((tour) => {
        tour.tracks.forEach((track) => {
          totalDistance += calculateTrackDistance(track)
        })
      })

      // Calculate total time moving
      let totalTime: number | null = null
      state.tours.forEach((tour) => {
        // Use tour-level time if available, otherwise sum from tracks
        if (tour.time_moving !== null && tour.time_moving !== undefined) {
          totalTime = (totalTime || 0) + tour.time_moving
        } else {
          tour.tracks.forEach((track) => {
            const trackTime = getTrackTimeMoving(track)
            if (trackTime && trackTime > 0) {
              totalTime = (totalTime || 0) + trackTime
            }
          })
        }
      })

      if (!totalTime || totalTime === 0 || totalDistance === 0) {
        return null
      }

      // Convert to km/h: (distance in meters / 1000) / (time in seconds / 3600)
      return totalDistance / 1000 / (totalTime / 3600)
    },

    currentWorkspace: (state) => ({
      clientId: state.clientId,
      name: state.name,
      isPublic: state.isPublic,
      _createEventPushed: state._createEventPushed,
      userRole: state.userRole,
      canEdit: state.canEdit,
      canManage: state.canManage,
      canView: state.canView,
      isBookmarked: state.isBookmarked,
      systemKey: state.systemKey,
      isSystem: state.isSystem,
      ownerNames: state.ownerNames,
    }),

    // Role-based permission getters.
    // canEditWorkspace = may contribute content (owner/collaborator).
    // canManageWorkspace = owner-only administration (settings, members, sharing); falls back to
    // the role for data hydrated before canManage existed (e.g. old offline snapshots).
    isOwner: (state) => state.userRole === 'Owner',
    isCollaborator: (state) => state.userRole === 'Collaborator',
    isViewer: (state) => state.userRole === 'Viewer',
    canEditWorkspace: (state) => state.canEdit === true,
    canViewWorkspace: (state) => state.canView === true,
    canManageWorkspace: (state) =>
      state.canManage === true || (state.canManage == null && state.userRole === 'Owner'),
    canShareWorkspace: (state) =>
      state.canManage === true || (state.canManage == null && state.userRole === 'Owner'),
  },

  actions: {
    setBookmarkState(isBookmarked: boolean) {
      this.isBookmarked = isBookmarked
    },

    /**
     * Initialize the store and load data from IndexedDB
     */
    async init() {
      // Set loading flag to true to prevent subscription events during initialization
      this._isLoading = true
      try {
        // Workspaces no longer persisted to IndexedDB - data comes from server sync or stays in memory
        // All workspace initialization will happen when loading from server via syncService

        // Tours no longer persisted to IndexedDB - data comes from server sync or stays in memory
        // All tours initialization will happen when loading from server via syncService

        // Set up subscription for state changes AFTER initial data is loaded
        this.$subscribe(async (mutation, state) => {
          // Skip if we're still in the loading phase
          if (this._isLoading) return

          const eventStore = useEventStore()

          // Only process events if we have a workspaceId and previous state is set
          if (!state.clientId || !this._previousState) return

          // Handle workspace property changes
          if (mutation.type === 'direct') {
            const currentState = {
              clientId: state.clientId,
              name: state.name,
              description: state.description,
              isPublic: state.isPublic,
            }

            // Check for changes in all relevant fields (excluding isPublic which is handled via API)
            const changes: { field: string; value: string | boolean | null }[] = []

            // Check each field individually for changes
            if (this._previousState.clientId !== currentState.clientId) {
              changes.push({ field: 'clientId', value: currentState.clientId })
            }
            if (this._previousState.name !== currentState.name) {
              changes.push({ field: 'name', value: currentState.name })
            }
            if (this._previousState.description !== currentState.description) {
              changes.push({ field: 'description', value: currentState.description || '' })
            }
            // Skip isPublic - handled directly via API endpoint

            // Create event if we have changes
            if (changes.length > 0) {
              const eventChanges: EventChange[] = changes.map((change) => ({
                field: change.field,
                value: change.value as string | number | boolean | File | null,
              }))

              eventStore.addEvent({
                type: EventType.WORKSPACE_UPDATED,
                payload: {
                  clientId: state.clientId,
                  changes: eventChanges,
                },
              })

              // Update snapshot after creating the event
              this._previousState = { ...currentState }
            }
          }
        })

        // Turn off loading flag AFTER subscription is set up
        this._isLoading = false
      } catch (error) {
        showError(
          `Failed to initialize workspace store: ${error instanceof Error ? error.message : String(error)}`,
        )
        this._error = error instanceof Error ? error.message : 'Failed to load data'
        this._isLoading = false
        throw error
      }
    },

    /**
     * Load track files (GPX, FIT) into the workspace
     * @param fileSource File list or array containing GPX and/or FIT files
     */
    async loadTrackFiles(fileSource: FileList | File[]): Promise<LoadTrackFilesResult> {
      throwIfOfflineReadOnly('upload tracks')

      this._isLoading = true
      this._error = null
      let lastId: string | null = null
      const newTourIds: string[] = []
      const newPOIIds: string[] = []
      const failedImports: TrackImportFailure[] = []
      const eventStore = useEventStore()
      const files = Array.from(fileSource)

      // Get the current tour count to use for color offset
      const initialTourCount = this.tours.length

      try {
        // Deactivate all existing tours - we'll activate only the last new one
        if (this.tours.length > 0) {
          this.tours.forEach((tour) => {
            tour.active = false
          })
        }

        // Track if we need to update the workspace name
        let workspaceNameUpdated = false

        return await eventStore.runInSyncBatch(async () => {
          for (let i = 0; i < files.length; i++) {
            const file = files[i]
            const fileName = file.name.toLowerCase()

            // Check if file is supported
            if (!fileName.endsWith('.gpx') && !fileName.endsWith('.fit')) {
              failedImports.push({
                fileName: file.name,
                reason: 'unsupported file type',
              })
              continue
            }

            // Check file size before processing using the global configuration
            if (file.size > CONFIG.UPLOAD_MAX_FILE_SIZE) {
              failedImports.push({
                fileName: file.name,
                reason: `exceeds the maximum size of ${formatByteSize(CONFIG.UPLOAD_MAX_FILE_SIZE)}`,
              })
              continue
            }

            try {
              let parsedTour

              // Parse file based on its extension
              if (fileName.endsWith('.gpx')) {
                const content = await file.arrayBuffer()
                parsedTour = await parseGPXFromArrayBuffer(content, file.name, file)
              } else if (fileName.endsWith('.fit')) {
                const content = await file.arrayBuffer()
                parsedTour = await parseFIT(content, file.name, file)
              }

              if (!parsedTour) {
                failedImports.push({
                  fileName: file.name,
                  reason: 'no tour data was parsed',
                })
                continue
              }

              const gpxGlobalWaypoints = fileName.endsWith('.gpx')
                ? (parsedTour.waypoints ?? [])
                : []
              if (fileName.endsWith('.gpx')) {
                parsedTour.waypoints = []
              }

              const importedWorkspacePOIs: WorkspacePOI[] = gpxGlobalWaypoints.map((waypoint) => {
                let timeValue: number | undefined = undefined
                if (waypoint.time) {
                  const timeAsNumber = Number(waypoint.time)
                  timeValue = isNaN(timeAsNumber)
                    ? new Date(waypoint.time as unknown as string).getTime()
                    : timeAsNumber
                }

                const poi: WorkspacePOI = {
                  clientId: generateId(),
                  workspaceClientId: this.clientId,
                  sourceWorkspaceClientId: this.clientId,
                  lat: waypoint.lat as number,
                  lon: waypoint.lon as number,
                  ele: waypoint.ele as number | undefined,
                  name: truncateToMaxLength(waypoint.name as string | undefined, 255, 'POI name'),
                  description: truncateToMaxLength(
                    waypoint.description as string | undefined,
                    2048,
                    'POI description',
                  ),
                  time: timeValue,
                  type: waypoint.type as string | undefined,
                  _previousState: undefined,
                }
                poi._previousState = poiSnapshot(poi)
                return poi
              })

              const hasTourContent =
                parsedTour.tracks.length > 0 || (parsedTour.routePoints?.length ?? 0) > 0

              if (!hasTourContent && importedWorkspacePOIs.length === 0) {
                failedImports.push({
                  fileName: file.name,
                  reason: 'no track or POI data was parsed',
                })
                continue
              }

              if (!hasTourContent) {
                for (const poi of importedWorkspacePOIs) {
                  const poiPayload = { ...poi }
                  delete poiPayload._previousState
                  await eventStore.addEvent({
                    type: EventType.WORKSPACE_POI_CREATED,
                    payload: poiPayload,
                  })
                  this.pois.push(poi)
                  newPOIIds.push(poi.clientId)
                }

                if (
                  this.tours.length === 0 &&
                  this.pois.length === importedWorkspacePOIs.length &&
                  this.name === newWorkspaceDefaultName
                ) {
                  const nextName =
                    truncateToMaxLength(
                      parsedTour.name || file.name.replace(/\.gpx$/i, ''),
                      255,
                      'Workspace name',
                    ) || 'Untitled Collection'
                  if (nextName !== this.name) {
                    this.name = nextName
                    workspaceNameUpdated = true
                  }
                }

                continue
              }

              const tourId = generateId()

              const successfulTourCount = newTourIds.length
              const importedTrackIdMap = new Map<string, string>()

              // Assign different colors to each track
              parsedTour.tracks = ensureTrackSegmentClientIds(parsedTour.tracks).map(
                (track, idx) => {
                  // Generate a unique ID for the track
                  const trackId = generateId()
                  if (track.clientId) {
                    importedTrackIdMap.set(track.clientId, trackId)
                  }

                  // Calculate color index based on successful tour order and track index
                  // so skipped imports do not create gaps in the palette.
                  const trackColor = TrackColorService.getColorByIndex(
                    initialTourCount + successfulTourCount + idx,
                  )

                  return {
                    ...track,
                    clientId: trackId,
                    visible: true,
                    color: trackColor,
                    _previousState: undefined, // Initialize snapshot for change detection
                  }
                },
              )

              // Assign IDs to waypoints
              parsedTour.waypoints = parsedTour.waypoints.map((waypoint) => {
                const waypointId = generateId()

                // Convert time from string to number if it exists
                let timeValue: number | undefined = undefined
                if (waypoint.time) {
                  // Try to parse as timestamp or convert date string to timestamp
                  const timeAsNumber = Number(waypoint.time)
                  timeValue = isNaN(timeAsNumber)
                    ? new Date(waypoint.time as unknown as string).getTime()
                    : timeAsNumber
                }

                return {
                  clientId: waypointId,
                  lat: waypoint.lat as number,
                  lon: waypoint.lon as number,
                  ele: waypoint.ele as number,
                  name: waypoint.name as string,
                  description: waypoint.description as string,
                  time: timeValue,
                  type: waypoint.type as string,
                  _previousState: undefined, // Initialize snapshot for change detection
                }
              })

              const importedRoutePoints = ensureImportedRoutePoints(
                parsedTour.routePoints || [],
                parsedTour.tracks,
                generateId,
              )

              // Assign IDs to route points
              parsedTour.routePoints = importedRoutePoints.map((rp, index) => {
                const mappedTrackClientId =
                  importedTrackIdMap.get(rp.trackClientId) ?? rp.trackClientId
                const nearestTrackClientId = findNearestTrackClientId(
                  parsedTour.tracks,
                  rp.lat as number,
                  rp.lon as number,
                )
                return {
                  clientId: generateId(),
                  lat: rp.lat as number,
                  lon: rp.lon as number,
                  ele: rp.ele as number | undefined,
                  name: rp.name as string | undefined,
                  description: rp.description as string | undefined,
                  order: (rp.order as number) ?? index,
                  trackClientId: requireTrackClientId(
                    mappedTrackClientId ?? nearestTrackClientId,
                    'Imported route point',
                  ),
                  _previousState: undefined,
                }
              })

              const tour: Tour = {
                ...parsedTour,
                clientId: tourId,
                type: getPersistedTourType(
                  parsedTour.type as string | undefined,
                  parsedTour.tracks as Array<Pick<Track, 'type'>>,
                ),
                visible: true,
                active: false, // Don't set active yet, we'll set only the last one active
                order: this.getNextTourOrder(),
                isPinned: isPinnedWorkspaceState(this) ? true : parsedTour.isPinned,
                _previousState: undefined, // Initialize snapshot for change detection
                _detailsLoaded: true,
              }

              const nextWorkspaceName =
                this.tours.length === 0 && this.name === newWorkspaceDefaultName
                  ? truncateToMaxLength(tour.name || 'Unnamed Tour', 255, 'Workspace name') ||
                    'Unnamed Tour'
                  : null

              const {
                _originalFile: eventFile,
                eventFileName,
                eventFileOriginalName,
                eventFileMimeType,
                eventFileCompressed,
                isPinned: _optimisticPinnedState,
                ...tourPayload
              } = tour

              const eventFileMetadata = {
                name: eventFileName || eventFile?.name || file.name,
                originalName: eventFileOriginalName || file.name,
                mimeType: eventFileMimeType || eventFile?.type || file.type,
                compressed: eventFileCompressed ?? false,
              }

              const originalSourceFilename = tour.sourceFilename || eventFileMetadata.originalName
              const sourceFilename =
                truncateToMaxLength(originalSourceFilename, 4096) ??
                originalSourceFilename.slice(0, 4096)
              tour.sourceFilename = sourceFilename

              const eventPayload: typeof tourPayload & {
                workspaceClientId: string
                sourceFilename: string
                eventFile?: File
                eventFileName: string
                eventFileOriginalName: string
                eventFileMimeType: string
                eventFileCompressed: boolean
              } = {
                workspaceClientId: this.clientId,
                ...tourPayload,
                sourceFilename,
                eventFileName: eventFileMetadata.name,
                eventFileOriginalName: eventFileMetadata.originalName,
                eventFileMimeType: eventFileMetadata.mimeType,
                eventFileCompressed: eventFileMetadata.compressed,
              }

              if (eventFile) {
                eventPayload.eventFile = eventFile
              }

              // Create a TOUR_CREATED event first (includes quota check)
              try {
                await eventStore.addEvent({
                  type: EventType.TOUR_CREATED,
                  payload: eventPayload,
                })

                await eventStore.addEvent({
                  type: EventType.TOUR_ADDED_TO_WORKSPACE,
                  payload: {
                    client_id: tour.clientId,
                    workspace_client_id: this.clientId,
                  },
                })

                for (const poi of importedWorkspacePOIs) {
                  const poiPayload = { ...poi }
                  delete poiPayload._previousState
                  await eventStore.addEvent({
                    type: EventType.WORKSPACE_POI_CREATED,
                    payload: poiPayload,
                  })
                  this.pois.push(poi)
                  newPOIIds.push(poi.clientId)
                }

                // Only add to UI state if event creation succeeded (quota check passed)
                this.tours.push(tour)
                newTourIds.push(tourId)
                lastId = tourId

                if (nextWorkspaceName && nextWorkspaceName !== this.name) {
                  this.name = nextWorkspaceName
                  workspaceNameUpdated = true
                }

                // Notify listeners that a tour was added.
                window.dispatchEvent(new CustomEvent('tour-added', { detail: { tourId } }))
              } catch (error) {
                // Quota check cancelled: skip the file, but record it as a failed import so the
                // summary toast names it — never a silent drop (sync-feedback.md §10.2).
                if (error instanceof Error && error.message === 'QUOTA_CHECK_CANCELLED') {
                  failedImports.push({
                    fileName: file.name,
                    reason: 'not saved — storage check was cancelled',
                  })
                  continue
                }
                // Re-throw other errors
                throw error
              }
            } catch (error) {
              logService.error(LOG_CATEGORIES.FILE, 'Failed to import track file', {
                fileName: file.name,
                error,
              })
              failedImports.push({
                fileName: file.name,
                reason: error instanceof Error ? error.message : String(error),
              })
              continue
            }
          }

          // After all files are processed, activate only the last tour
          if (newTourIds.length > 0) {
            const lastNewTourId = newTourIds[newTourIds.length - 1]
            this.setTourActive(lastNewTourId, true)
          }

          if (lastId) {
            this._lastAddedTourId = lastId
          }

          // Create a WORKSPACE_UPDATED event if the workspace name was updated from a tour
          if (workspaceNameUpdated && this.clientId) {
            await eventStore.addEvent({
              type: EventType.WORKSPACE_UPDATED,
              payload: {
                clientId: this.clientId,
                changes: [{ field: 'name', value: this.name || '' }],
              },
            })
          }

          const importFailureSummary = formatTrackImportFailureSummary(
            failedImports,
            newTourIds.length,
          )
          if (importFailureSummary) {
            showError(importFailureSummary)
            this._error = newTourIds.length === 0 ? importFailureSummary : null
          }

          // Confirm what DID import — and be honest that syncing continues in the background
          // (docs/concepts/sync-feedback.md §4.3). Skipped while the sync dialog is open:
          // it already lists the imported items with live status (no double feedback).
          if (newTourIds.length > 0 && !syncDialogVisible.value) {
            showSuccess(
              importSuccessMessage(
                newTourIds.length,
                connectivityService.isEffectivelyOnline.value,
              ),
            )
          }

          // No persistence to IndexedDB - data stays in memory only
          return {
            importedTourIds: newTourIds,
            importedPOIIds: newPOIIds,
            failedImports,
          }
        })
      } catch (err) {
        showError(
          `Error processing track files: ${err instanceof Error ? err.message : String(err)}`,
        )
        this._error = err instanceof Error ? err.message : 'Failed to process track files'
        throw err
      } finally {
        this._isLoading = false
      }
    },

    /**
     * Add a new empty tour to the workspace
     */
    async addEmptyTour(input?: {
      name?: string
      description?: string
      type?: string
      sourceTourClientId?: string | null
      sourceTour?: TourSource | null
      emitAddedEvent?: boolean
    }) {
      throwIfOfflineReadOnly('create tour')

      const eventStore = useEventStore()
      const tourId = generateId()
      const tour = buildEmptyTour(tourId, input, { order: this.getNextTourOrder() })
      if (isPinnedWorkspaceState(this)) {
        tour.isPinned = true
      }
      const { sourceTour: _sourceTour, isPinned: _optimisticPinnedState, ...tourPayload } = tour

      const eventPayload = {
        workspaceClientId: this.clientId,
        ...tourPayload,
        eventFileName: 'generated',
        eventFileOriginalName: 'generated',
        eventFileMimeType: 'application/json',
        eventFileCompressed: false,
      }

      await eventStore.runInSyncBatch(async () => {
        // Create a TOUR_CREATED event first (includes quota check)
        await eventStore.addEvent({
          type: EventType.TOUR_CREATED,
          payload: eventPayload,
        })

        await eventStore.addEvent({
          type: EventType.TOUR_ADDED_TO_WORKSPACE,
          payload: {
            client_id: tour.clientId,
            workspace_client_id: this.clientId,
          },
        })
      })

      // Only add to UI state if event creation succeeded
      this.tours.push(tour)

      // Emit event to trigger zoom or selection
      if (input?.emitAddedEvent !== false) {
        window.dispatchEvent(new CustomEvent('tour-added', { detail: { tourId } }))
      }

      return tourId
    },

    addLocalDraftTour(input?: { name?: string; description?: string; type?: string }) {
      const tourId = generateId()
      const tour = buildEmptyTour(tourId, input, {
        order: this.getNextTourOrder(),
        localOnlyDraft: true,
      })
      if (isPinnedWorkspaceState(this)) {
        tour.isPinned = true
      }

      this.tours.push(tour)
      return tourId
    },

    /**
     * Promote a local-only draft tour into real sync events.
     *
     * A tour started by drawing (`addLocalDraftTour`) carries `_localOnlyDraft` and emits NO
     * events until the drawn route is saved. Any operation that references such a tour (rename,
     * collection rename, waypoint, reorder…) would otherwise enqueue an event whose parent
     * `TOUR_CREATED` is not yet in the queue, and the backend rejects it → the frontend quarantines
     * it → the operation is silently lost. This flushes the deferred `TOUR_CREATED` +
     * `TOUR_ADDED_TO_WORKSPACE` first so the create always precedes the reference in the queue.
     * No-op once the tour has been promoted. Mirrors the lazy `WORKSPACE_CREATED` mechanism.
     * See docs/concepts/sync-ordering-edge-cases.md (Group 1).
     */
    async _promoteLocalDraftTour(tour: Tour) {
      if (!tour._localOnlyDraft) return

      const eventStore = useEventStore()
      const {
        sourceTour: _sourceTour,
        isPinned: _optimisticPinnedState,
        ...tourPayload
      } = clonePlain(tour)
      const tourEventPayload = {
        workspaceClientId: this.clientId,
        ...tourPayload,
        tracks: [],
        waypoints: [],
        routePoints: [],
        eventFileName: 'generated',
        eventFileOriginalName: 'generated',
        eventFileMimeType: 'application/json',
        eventFileCompressed: false,
      }

      await eventStore.addEvent({
        type: EventType.TOUR_CREATED,
        payload: tourEventPayload,
      })

      await eventStore.addEvent({
        type: EventType.TOUR_ADDED_TO_WORKSPACE,
        payload: {
          client_id: tour.clientId,
          workspace_client_id: this.clientId,
        },
      })

      tour._localOnlyDraft = false
    },

    // Add a tour directly (e.g., from a sync event)
    async addTourFromEvent(tour: Tour) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        // Ensure the tour has a local ID if not already present
        if (!tour.clientId) {
          tour.clientId = generateId()
        }

        // Check if we already have this tour
        const existingTour = this.tours.find((t) => t.clientId === tour.clientId)

        if (existingTour) {
          // Tour exists, update it
          await this.updateTourFromEvent(existingTour.clientId, tour)
        } else {
          tour._detailsLoaded = tour._detailsLoaded ?? true
          applyPersistedWorkspaceTourVisibility(this.clientId, [tour])
          this.tours.push(tour)

          // Initialize snapshot for change detection
          tour._previousState = undefined

          // Save tour to IndexedDB
          // No persistence to IndexedDB - data stays in memory only
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    // Add a tour from server (used in full sync)
    async addTourFromServer(tour: Record<string, unknown>) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        const newTour: Tour = {
          clientId: generateId(),
          name: truncateToMaxLength(tour.name as string, 255, 'Tour name') || 'Unnamed Tour',
          description:
            truncateToMaxLength(tour.description as string, 2048, 'Tour description') || '',
          type: getPersistedTourType(
            tour.type as string | null | undefined,
            ((tour.tracks as Record<string, unknown>[] | undefined) ?? []).map((track) => ({
              type: track.type as string | undefined,
            })),
          ),
          tracks: [],
          waypoints: [],
          routePoints: [],
          visible: true,
          active: false,
          order: (tour.order as number) || this.getNextTourOrder(),
          routingProfile: tour.routingProfile as string | undefined,
          distance: (tour.distance as number) || null,
          time_moving: (tour.time_moving as number) || null,
          time_elapsed: (tour.time_elapsed as number) || null,
          _previousState: undefined, // Initialize snapshot for change detection
          _detailsLoaded: true,
        }

        // Process tracks
        if (tour.tracks && Array.isArray(tour.tracks)) {
          newTour.tracks = (tour.tracks as Record<string, unknown>[]).map((track, idx) => {
            const newTrack: Track = {
              clientId: (track.clientId as string | undefined) || generateId(),
              name:
                truncateToMaxLength(track.name as string, 255, 'Track name') || `Track ${idx + 1}`,
              segments: hydrateApiSegments((track.segments as Track['segments']) || []),
              pointsLineReduced5m: track.pointsLineReduced5m as string | undefined,
              pointsLineReduced500m: track.pointsLineReduced500m as string | undefined,
              visible: track.visible !== undefined ? (track.visible as boolean) : true,
              color: TrackColorService.ensureValidColor(track.color as string | undefined),
              metrics: (track.metrics as Track['metrics']) || undefined,
              routingProfile: track.routingProfile as string | undefined,
              type: track.type as string | undefined,
              _previousState: undefined, // Initialize snapshot for change detection
            }

            return newTrack
          })
        }

        // Process waypoints
        if (tour.waypoints && Array.isArray(tour.waypoints)) {
          newTour.waypoints = (tour.waypoints as Record<string, unknown>[]).map((waypoint) => {
            const waypointId = generateId()

            // Convert time from string to number if it exists
            let timeValue: number | undefined = undefined
            if (waypoint.time) {
              // Try to parse as timestamp or convert date string to timestamp
              const timeAsNumber = Number(waypoint.time)
              timeValue = isNaN(timeAsNumber)
                ? new Date(waypoint.time as unknown as string).getTime()
                : timeAsNumber
            }

            return {
              clientId: waypointId,
              lat: waypoint.lat as number,
              lon: waypoint.lon as number,
              ele: waypoint.ele as number,
              name: truncateToMaxLength(waypoint.name as string, 255, 'Waypoint name'),
              desc: truncateToMaxLength(waypoint.desc as string, 2048, 'Waypoint description'),
              time: timeValue,
              type: waypoint.type as string,
              _previousState: undefined, // Initialize snapshot for change detection
            }
          })
        }

        // Process route points
        if (tour.route_points && Array.isArray(tour.route_points)) {
          newTour.routePoints = (tour.route_points as Record<string, unknown>[]).map(
            (rp, index) => ({
              clientId: (rp.client_id as string) || generateId(),
              lat: rp.lat as number,
              lon: rp.lon as number,
              ele: rp.ele as number | undefined,
              name: truncateToMaxLength(rp.name as string, 255, 'Route point name'),
              description: truncateToMaxLength(rp.desc as string, 2048, 'Route point description'),
              order: (rp.order as number) ?? index,
              trackClientId: getRoutePointTrackClientId(rp, 'Workspace route point'),
              _previousState: undefined,
            }),
          )
        }

        this.tours.push(newTour)

        // Save tour to IndexedDB
        // No persistence to IndexedDB - data stays in memory only
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    // Update a tour from an event
    async updateTourFromEvent(tourId: string, changes: Partial<Tour>) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        const tour = this.tours.find((tour) => tour.clientId === tourId)
        if (tour) {
          Object.assign(tour, changes)
          applyPersistedWorkspaceTourVisibility(this.clientId, [tour])

          // Save updated tour to IndexedDB
          // No persistence to IndexedDB - data stays in memory only
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    // Rename a tour from an event
    async renameTourFromEvent(tourId: string, newName: string) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        const tour = this.tours.find((tour) => tour.clientId === tourId)
        if (tour) {
          tour.name = truncateToMaxLength(newName, 255, 'Tour name') || newName
          // No persistence to IndexedDB - data stays in memory only
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    async clearTour(tourId: string) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)

      if (tour) {
        // A local-only draft was never synced (no TOUR_CREATED emitted). Drop it locally and
        // emit nothing — a TOUR_REMOVED_FROM_WORKSPACE would reference a tour the server has
        // never seen and get quarantined.
        if (tour._localOnlyDraft) {
          const draftIndex = this.tours.findIndex((t) => t.clientId === tourId)
          if (draftIndex !== -1) {
            this.tours.splice(draftIndex, 1)
          }
          if (this._selectedTourId === tourId) {
            this._selectedTourId = null
          }
          if (this._loadingTourDetailId === tourId) {
            this._loadingTourDetailId = null
          }
          if (this._lastAddedTourId === tourId) {
            this._lastAddedTourId = null
          }
          return
        }

        // Unlinking (unlike editing) is curation: the collection owner or the tour's own
        // creator may do it — mirrors the backend's validate_workspace_owner_or_creator.
        if (
          !(await requireItemEditable(
            this.isOwner ? true : tour.canEdit,
            tour.type ? 'activity' : 'route',
            tour.createdByName,
          ))
        )
          return

        // Mark tour as pending deletion
        tour._pendingDeletion = true
        // No persistence to IndexedDB - data stays in memory only

        try {
          // Send the DELETED event
          await eventStore.addEvent({
            type: EventType.TOUR_REMOVED_FROM_WORKSPACE,
            payload: {
              client_id: tourId,
              workspace_client_id: this.clientId,
            },
          })

          // After successful event, mark as deleted
          tour._pendingDeletion = true
          // No persistence to IndexedDB - data stays in memory only

          // Now remove the tour from the array
          const index = this.tours.findIndex((t) => t.clientId === tourId)
          if (index !== -1) {
            this.tours.splice(index, 1)
          }

          // Clean up selectedTour and lastAddedTour references if needed
          if (this._selectedTourId === tourId) {
            this._selectedTourId = null
          }
          if (this._loadingTourDetailId === tourId) {
            this._loadingTourDetailId = null
          }
          if (this._lastAddedTourId === tourId) {
            this._lastAddedTourId = null
          }

          // Finally delete from the database
          // No deletion from IndexedDB - data is memory-only
        } catch (error) {
          // In case of error, revert the pending deletion flag
          tour._pendingDeletion = false
          // No persistence to IndexedDB - data stays in memory only
          throw error
        }
      }
    },

    async clearAllTours() {
      const eventStore = useEventStore()

      // Create events for each tour before clearing
      // Use [...this.tours] to create a copy, as clearTour modifies the array
      await eventStore.runInSyncBatch(async () => {
        for (const tour of [...this.tours]) {
          await this.clearTour(tour.clientId)
        }
      })

      // The actual clearing is now handled by the repeated calls to clearTour
    },

    async setTourActive(tourId: string, active: boolean) {
      // If activating this tour, deactivate all others first (radio button behavior)
      if (active) {
        for (const tour of this.tours) {
          const wasActive = tour.active
          tour.active = tour.clientId === tourId

          // Only save tours that changed
          if (wasActive !== tour.active) {
            // No persistence to IndexedDB - data stays in memory only
          }
        }
      } else {
        // Just deactivate this tour
        const tour = this.tours.find((tour) => tour.clientId === tourId)
        if (tour && tour.active) {
          tour.active = false
          // No persistence to IndexedDB - data stays in memory only
        }
      }
    },

    async setTourVisibility(tourId: string, visible: boolean) {
      const tour = this.tours.find((tour) => tour.clientId === tourId)

      if (tour) {
        tour.visible = visible

        // Also update visibility for all tracks in the tour
        // Note: Visibility changes are intentionally not synced to the server
        for (const track of tour.tracks) {
          track.visible = visible
        }

        persistWorkspaceTourVisibility(this.clientId, this.tours)
        // No persistence to IndexedDB - visibility stays local-only in localStorage
      }
    },

    async updateTour(
      tourId: string,
      updates: {
        name?: string
        description?: string
        color?: string
        type?: string
        routingProfile?: string | null
      },
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((tour) => tour.clientId === tourId)

      if (tour) {
        if (
          !(await requireItemEditable(
            tour.canEdit,
            tour.type ? 'activity' : 'route',
            tour.createdByName,
          ))
        )
          return

        // Get current state for all tour fields (only flat properties)
        const currentState = {
          clientId: tour.clientId,
          name: tour.name,
          description: tour.description,
          type: tour.type,
          routingProfile: tour.routingProfile,
          visible: tour.visible,
          active: tour.active,
          expanded: tour.expanded,
        }

        // Apply updates to current state to get new state
        const newState = {
          clientId: tour.clientId,
          name: updates.name !== undefined ? updates.name : tour.name,
          description: updates.description !== undefined ? updates.description : tour.description,
          type: updates.type !== undefined ? updates.type : tour.type,
          routingProfile:
            updates.routingProfile !== undefined
              ? (updates.routingProfile ?? undefined)
              : tour.routingProfile,
          visible: tour.visible,
          active: tour.active,
          expanded: tour.expanded,
          order: tour.order,
          distance: tour.distance,
          time_moving: tour.time_moving,
          time_elapsed: tour.time_elapsed,
        }

        // Check for changes in all fields
        const changes: { field: string; value: string | null }[] = []

        if (currentState.name !== newState.name) {
          changes.push({ field: 'name', value: newState.name })
        }
        if (currentState.description !== newState.description) {
          changes.push({ field: 'description', value: newState.description || '' })
        }
        if (currentState.type !== newState.type) {
          changes.push({ field: 'type', value: newState.type ?? null })
        }
        if (currentState.routingProfile !== newState.routingProfile) {
          changes.push({ field: 'routingProfile', value: newState.routingProfile ?? null })
        }

        // Create event if we have changes
        if (changes.length > 0) {
          const eventChanges: EventChange[] = changes.map((change) => ({
            field: change.field,
            value: change.value as string | number | boolean | File | null,
          }))

          await eventStore.runInSyncBatch(async () => {
            // Ensure a drawn-but-unsaved draft has its TOUR_CREATED queued before this update.
            await this._promoteLocalDraftTour(tour)

            await eventStore.addEvent({
              type: EventType.TOUR_UPDATED,
              payload: {
                clientId: tourId,
                changes: eventChanges,
              },
            })
          })

          // Update snapshot after creating the event
          tour._previousState = { ...newState }
        }

        // Apply the updates to the tour
        if (updates.name !== undefined)
          tour.name = truncateToMaxLength(updates.name, 255, 'Tour name') || updates.name
        if (updates.description !== undefined)
          tour.description =
            truncateToMaxLength(updates.description, 2048, 'Tour description') ||
            updates.description
        if (updates.type !== undefined) {
          tour.type = updates.type
        }
        if (updates.routingProfile !== undefined) {
          tour.routingProfile = updates.routingProfile ?? undefined
        }

        // No persistence to IndexedDB - data stays in memory only
      }

      return this.clientId
    },

    setTourCollectionNameOverride(tourId: string, name: string | null) {
      const tour = this.tours.find((candidate) => candidate.clientId === tourId)
      if (!tour) return

      const trimmedName = name?.trim() || null
      tour.customName = trimmedName
      tour.collectionName = trimmedName ?? tour.name
    },

    async updateTourCollectionName(tourId: string, name: string | null) {
      const eventStore = useEventStore()
      const tour = this.tours.find((candidate) => candidate.clientId === tourId)
      if (!tour) return this.clientId

      // Per-collection presentation is curation, not content: the collection owner or the
      // tour's own creator may set it (mirrors validate_workspace_owner_or_creator).
      if (
        !(await requireItemEditable(
          this.isOwner ? true : tour.canEdit,
          tour.type ? 'activity' : 'route',
          tour.createdByName,
        ))
      )
        return this.clientId

      const previousCustomName = tour.customName ?? null
      const previousCollectionName = tour.collectionName ?? tour.name
      const trimmedName = name?.trim() || null
      const nextCollectionName = trimmedName ?? tour.name

      if (previousCustomName === trimmedName && previousCollectionName === nextCollectionName) {
        return this.clientId
      }

      this.setTourCollectionNameOverride(tourId, trimmedName)

      try {
        await eventStore.runInSyncBatch(async () => {
          // A collection rename targets the membership row (created by TOUR_ADDED_TO_WORKSPACE);
          // promote a draft first so both the tour and its membership exist server-side.
          await this._promoteLocalDraftTour(tour)

          await eventStore.addEvent({
            type: EventType.TOUR_MEMBERSHIP_UPDATED,
            payload: {
              client_id: tourId,
              workspace_client_id: this.clientId,
              changes: [{ field: 'custom_name', value: trimmedName }],
            },
          })
        })
      } catch (error) {
        tour.customName = previousCustomName
        tour.collectionName = previousCollectionName
        throw error
      }

      return this.clientId
    },

    async deleteWaypoint(tourId: string, waypointIndex: number) {
      const eventStore = useEventStore()
      const uiStore = useUiStore()
      const tour = this.tours.find((t) => t.clientId === tourId)

      if (tour && waypointIndex >= 0 && waypointIndex < tour.waypoints.length) {
        if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return

        const waypoint = tour.waypoints[waypointIndex]

        // Clear hover state for this waypoint if it's currently hovered
        const waypointId = `${tourId}_${waypointIndex}`
        if (uiStore.hoveredWaypointId === waypointId) {
          uiStore.setHoveredWaypoint(null)
        }

        // Optimistic UI update: Remove waypoint immediately for responsive UI
        const removedWaypoint = tour.waypoints.splice(waypointIndex, 1)[0]

        // Handle async event creation in background
        try {
          // Send the DELETED event (async operations run in background)
          await eventStore.addEvent({
            type: EventType.WAYPOINT_DELETED,
            payload: {
              clientId: waypoint.clientId,
            },
          })
        } catch (error) {
          // On error, restore the waypoint at its original position
          tour.waypoints.splice(waypointIndex, 0, removedWaypoint)

          // Show error to user
          showError(
            `Failed to delete waypoint: ${error instanceof Error ? error.message : String(error)}`,
          )
          throw error
        }
      }
    },

    async deleteAllWaypoints() {
      const eventStore = useEventStore()
      const uiStore = useUiStore()

      for (const tour of this.tours.filter((candidate) => candidate.waypoints.length > 0)) {
        if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return
      }

      // Clear any hovered waypoint state since all waypoints will be deleted
      uiStore.setHoveredWaypoint(null)

      // Collect all waypoints to delete before UI updates
      const waypointsToDelete: Array<{ tour: Tour; waypoint: Waypoint }> = []
      for (const tour of this.tours) {
        for (const waypoint of tour.waypoints) {
          waypointsToDelete.push({ tour, waypoint })
        }
      }

      // Optimistic UI update: Clear all waypoints immediately for responsive UI
      for (const tour of this.tours) {
        tour.waypoints = []
      }

      // Handle async event creation in background
      try {
        // Create WAYPOINT_DELETED events for all waypoints
        for (const { waypoint } of waypointsToDelete) {
          await eventStore.addEvent({
            type: EventType.WAYPOINT_DELETED,
            payload: {
              clientId: waypoint.clientId,
            },
          })
        }
      } catch (error) {
        // On error, restore all waypoints to their original tours
        for (const { tour, waypoint } of waypointsToDelete) {
          tour.waypoints.push(waypoint)
        }

        // Show error to user
        showError(
          `Failed to delete waypoints: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }
    },

    async addRoutePoint(
      tourId: string,
      routePointInput: {
        clientId?: string
        lat: number
        lon: number
        ele?: number
        name?: string
        description?: string
        order?: number
        trackClientId?: string
      },
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return null
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null

      const clientId = routePointInput.clientId ?? generateId()
      const routePoint: RoutePoint = {
        clientId,
        lat: routePointInput.lat,
        lon: routePointInput.lon,
        ele: routePointInput.ele,
        name: routePointInput.name
          ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
          : undefined,
        description: routePointInput.description
          ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
          : undefined,
        trackClientId: requireTrackClientId(routePointInput.trackClientId, 'New route point'),
        order:
          routePointInput.order ??
          getSparseTrackAppendOrder(tour.routePoints, routePointInput.trackClientId),
      }

      await eventStore.addEvent({
        type: EventType.ROUTE_POINT_CREATED,
        payload: routePoint,
      })

      tour.routePoints.push(routePoint)
      return clientId
    },

    async insertRoutePointAt(
      tourId: string,
      insertIndex: number,
      routePointInput: {
        clientId?: string
        lat: number
        lon: number
        ele?: number
        name?: string
        description?: string
        trackClientId?: string
      },
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return null
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null
      if (insertIndex < 0 || insertIndex > tour.routePoints.length) return null

      const trackClientId = requireTrackClientId(
        routePointInput.trackClientId ??
          (insertIndex > 0 ? tour.routePoints[insertIndex - 1].trackClientId : undefined) ??
          (insertIndex < tour.routePoints.length
            ? tour.routePoints[insertIndex].trackClientId
            : undefined),
        'Inserted route point',
      )

      const insertPlan = getRoutePointInsertOrderPlan(tour.routePoints, insertIndex, trackClientId)
      if (!insertPlan) return null

      const clientId = routePointInput.clientId ?? generateId()
      const routePoint: RoutePoint = {
        clientId,
        lat: routePointInput.lat,
        lon: routePointInput.lon,
        ele: routePointInput.ele,
        name: routePointInput.name
          ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
          : undefined,
        description: routePointInput.description
          ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
          : undefined,
        order: insertPlan.insertOrder,
        trackClientId,
      }

      tour.routePoints.splice(insertIndex, 0, routePoint)
      insertPlan.shifts.forEach(({ routePoint: existing, newOrder }) => {
        existing.order = newOrder
      })

      await eventStore.runInSyncBatch(async () => {
        for (const { routePoint: existing, newOrder } of insertPlan.shifts) {
          await eventStore.queueRoutePointUpdate(existing.clientId, [
            { field: 'order', value: newOrder },
          ])
        }

        await eventStore.addEvent({
          type: EventType.ROUTE_POINT_CREATED,
          payload: routePoint,
        })
      })
      return clientId
    },

    async insertRoutePointsAt(
      tourId: string,
      insertIndex: number,
      routePointInputs: Array<{
        clientId?: string
        lat: number
        lon: number
        ele?: number
        name?: string
        description?: string
        trackClientId?: string
      }>,
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return []
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return []
      if (insertIndex < 0 || insertIndex > tour.routePoints.length) return []
      if (routePointInputs.length === 0) return []

      const trackClientId = requireTrackClientId(
        routePointInputs[0]?.trackClientId ??
          (insertIndex > 0 ? tour.routePoints[insertIndex - 1].trackClientId : undefined) ??
          (insertIndex < tour.routePoints.length
            ? tour.routePoints[insertIndex].trackClientId
            : undefined),
        'Inserted route points',
      )

      const insertPlan = getRoutePointMultiInsertOrderPlan(
        tour.routePoints,
        insertIndex,
        routePointInputs.length,
        trackClientId,
      )
      if (!insertPlan) return []

      const routePoints = routePointInputs.map((routePointInput, index) => ({
        clientId: routePointInput.clientId ?? generateId(),
        lat: routePointInput.lat,
        lon: routePointInput.lon,
        ele: routePointInput.ele,
        name: routePointInput.name
          ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
          : undefined,
        description: routePointInput.description
          ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
          : undefined,
        order: insertPlan.insertOrders[index] ?? insertPlan.insertOrderStart + index,
        trackClientId,
      }))

      tour.routePoints.splice(insertIndex, 0, ...routePoints)
      insertPlan.shifts.forEach(({ routePoint: existing, newOrder }) => {
        existing.order = newOrder
      })

      await eventStore.runInSyncBatch(async () => {
        for (const { routePoint: existing, newOrder } of insertPlan.shifts) {
          await eventStore.queueRoutePointUpdate(existing.clientId, [
            { field: 'order', value: newOrder },
          ])
        }

        for (const routePoint of routePoints) {
          await eventStore.addEvent({
            type: EventType.ROUTE_POINT_CREATED,
            payload: routePoint,
          })
        }
      })
      return routePoints.map((routePoint) => routePoint.clientId)
    },

    async deleteRoutePoint(tourId: string, routePointIndex: number) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)

      if (tour && routePointIndex >= 0 && routePointIndex < tour.routePoints.length) {
        if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
        const routePoint = tour.routePoints[routePointIndex]

        // Optimistic UI update
        const removedRoutePoint = tour.routePoints.splice(routePointIndex, 1)[0]

        try {
          await eventStore.runInSyncBatch(async () => {
            await eventStore.queueRoutePointDelete(routePoint.clientId)
          })
        } catch (error) {
          // On error, restore the route point
          tour.routePoints.splice(routePointIndex, 0, removedRoutePoint)
          showError(
            `Failed to delete route point: ${error instanceof Error ? error.message : String(error)}`,
          )
          throw error
        }
      }
    },

    async deleteRoutePoints(tourId: string, routePointIndices: number[]) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return 0
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return 0

      const { removedRoutePoints } = getRoutePointMultiDeletePlan(
        tour.routePoints,
        routePointIndices,
      )
      if (removedRoutePoints.length === 0) return 0

      const originalRoutePoints = [...tour.routePoints]
      const previousOrders = new Map(
        originalRoutePoints.map((routePoint) => [routePoint.clientId, routePoint.order]),
      )
      const removedRoutePointIds = new Set(
        removedRoutePoints.map((routePoint) => routePoint.clientId),
      )

      tour.routePoints = originalRoutePoints.filter(
        (routePoint) => !removedRoutePointIds.has(routePoint.clientId),
      )
      try {
        await eventStore.runInSyncBatch(async () => {
          for (const routePoint of removedRoutePoints) {
            await eventStore.queueRoutePointDelete(routePoint.clientId)
          }
        })
      } catch (error) {
        originalRoutePoints.forEach((routePoint) => {
          const previousOrder = previousOrders.get(routePoint.clientId)
          if (previousOrder !== undefined) {
            routePoint.order = previousOrder
          }
        })
        tour.routePoints = originalRoutePoints
        showError(
          `Failed to delete route points: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }

      return removedRoutePoints.length
    },

    async reorderTrackRoutePoints(
      tourId: string,
      trackClientId: string,
      orderedRoutePointClientIds: string[],
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const originalRoutePoints = [...tour.routePoints]
      const trackRoutePoints = originalRoutePoints.filter(
        (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
      )
      if (trackRoutePoints.length === 0) return
      if (orderedRoutePointClientIds.length !== trackRoutePoints.length) {
        throw new Error('Failed to reorder route points: route point count mismatch')
      }

      const previousOrders = new Map(
        trackRoutePoints.map((routePoint) => [routePoint.clientId, routePoint.order] as const),
      )
      const reorderPlan = getSparseTrackReorderPlan(
        tour.routePoints,
        trackClientId,
        orderedRoutePointClientIds,
      )
      const reorderedTrackRoutePoints = reorderPlan.reorderedRoutePoints
      reorderPlan.changedRoutePoints.forEach(({ routePoint, newOrder }) => {
        routePoint.order = newOrder
      })

      let reorderedTrackIndex = 0
      tour.routePoints = originalRoutePoints.map((routePoint) =>
        (routePoint.trackClientId ?? null) === trackClientId
          ? reorderedTrackRoutePoints[reorderedTrackIndex++]
          : routePoint,
      )

      try {
        await eventStore.runInSyncBatch(async () => {
          const routePointOrderUpdates = reorderPlan.changedRoutePoints
            .map<RoutePointOrderEmission | null>(({ routePoint, newOrder }) => {
              const previousOrder = previousOrders.get(routePoint.clientId)
              if (previousOrder === undefined) return null
              return {
                clientId: routePoint.clientId,
                previousOrder,
                nextOrder: newOrder,
                finalChanges: [{ field: 'order', value: newOrder }],
              }
            })
            .filter((update): update is RoutePointOrderEmission => update !== null)

          await emitRoutePointOrderChanges(
            (clientId, changes) => eventStore.queueRoutePointUpdate(clientId, changes),
            routePointOrderUpdates,
            trackRoutePoints.map((routePoint) => previousOrders.get(routePoint.clientId) ?? -1),
            reorderPlan.changedRoutePoints.map(({ newOrder }) => newOrder),
          )
        })
      } catch (error) {
        trackRoutePoints.forEach((routePoint) => {
          const previousOrder = previousOrders.get(routePoint.clientId)
          if (previousOrder !== undefined) {
            routePoint.order = previousOrder
          }
        })
        tour.routePoints = originalRoutePoints
        showError(
          `Failed to reorder route points: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }
    },

    async deleteAllRoutePoints(tourId?: string) {
      const eventStore = useEventStore()

      const toursToProcess = tourId ? this.tours.filter((t) => t.clientId === tourId) : this.tours

      for (const tour of toursToProcess.filter((candidate) => candidate.routePoints.length > 0)) {
        if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
      }

      const routePointsToDelete: Array<{ tour: Tour; routePoint: RoutePoint }> = []
      for (const tour of toursToProcess) {
        for (const routePoint of tour.routePoints) {
          routePointsToDelete.push({ tour, routePoint })
        }
      }

      // Optimistic UI update
      for (const tour of toursToProcess) {
        tour.routePoints = []
      }

      try {
        await eventStore.runInSyncBatch(async () => {
          for (const { routePoint } of routePointsToDelete) {
            await eventStore.queueRoutePointDelete(routePoint.clientId)
          }
        })
      } catch (error) {
        // On error, restore route points
        for (const { tour, routePoint } of routePointsToDelete) {
          tour.routePoints.push(routePoint)
        }
        showError(
          `Failed to delete route points: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }
    },

    async updateWaypoint(
      tourId: string,
      waypointIndex: number,
      updatedWaypoint: Partial<Waypoint>,
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)

      if (tour && waypointIndex >= 0 && waypointIndex < tour.waypoints.length) {
        if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return

        const waypoint = tour.waypoints[waypointIndex]
        // Ensure waypointId is a string with a fallback ID if missing
        const waypointId = waypoint.clientId || generateId()

        // Get current state for all waypoint fields (only flat properties)
        const currentState = {
          clientId: waypoint.clientId,
          name: waypoint.name,
          description: waypoint.description,
          lat: waypoint.lat,
          lon: waypoint.lon,
          ele: waypoint.ele,
          time: waypoint.time,
          type: waypoint.type,
        }

        // Apply updates to current state to get new state
        const newState = {
          clientId: waypoint.clientId,
          name:
            updatedWaypoint.name !== undefined
              ? truncateToMaxLength(updatedWaypoint.name, 255, 'Waypoint name')
              : waypoint.name,
          description:
            updatedWaypoint.description !== undefined
              ? truncateToMaxLength(updatedWaypoint.description, 2048, 'Waypoint description')
              : waypoint.description,
          lat: updatedWaypoint.lat !== undefined ? updatedWaypoint.lat : waypoint.lat,
          lon: updatedWaypoint.lon !== undefined ? updatedWaypoint.lon : waypoint.lon,
          ele: updatedWaypoint.ele !== undefined ? updatedWaypoint.ele : waypoint.ele,
          time: updatedWaypoint.time !== undefined ? updatedWaypoint.time : waypoint.time,
          type: updatedWaypoint.type !== undefined ? updatedWaypoint.type : waypoint.type,
        }

        // Initialize previous state if it doesn't exist
        if (!waypoint._previousState) {
          waypoint._previousState = { ...currentState }
        }

        // Check for changes in all fields
        const changes: { field: string; value: string | number | undefined }[] = []

        if (waypoint._previousState.name !== newState.name) {
          changes.push({ field: 'name', value: newState.name })
        }
        if (waypoint._previousState.description !== newState.description) {
          changes.push({ field: 'description', value: newState.description })
        }
        if (waypoint._previousState.lat !== newState.lat) {
          changes.push({ field: 'lat', value: newState.lat })
        }
        if (waypoint._previousState.lon !== newState.lon) {
          changes.push({ field: 'lon', value: newState.lon })
        }
        if (waypoint._previousState.ele !== newState.ele) {
          changes.push({ field: 'ele', value: newState.ele })
        }
        if (waypoint._previousState.time !== newState.time) {
          changes.push({ field: 'time', value: newState.time })
        }
        if (waypoint._previousState.type !== newState.type) {
          changes.push({ field: 'type', value: newState.type })
        }

        // Create event if we have changes
        if (changes.length > 0) {
          const eventChanges: EventChange[] = changes.map((change) => ({
            field: change.field,
            value: change.value as string | number | boolean | File | null,
          }))

          await eventStore.addEvent({
            type: EventType.WAYPOINT_UPDATED,
            payload: {
              clientId: waypointId,
              changes: eventChanges,
            },
          })

          // Update snapshot after creating the event
          waypoint._previousState = { ...newState }
        }

        Object.assign(waypoint, updatedWaypoint)

        // No persistence to IndexedDB - data stays in memory only
      }
    },

    /**
     * Add a new waypoint to a tour and emit a WAYPOINT_CREATED event
     */
    async addWaypoint(
      tourId: string,
      waypointInput: {
        lat: number
        lon: number
        ele?: number | null
        name?: string
        description?: string
        time?: number
        type?: string
      },
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return null
      if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return null

      const clientId = generateId()
      const waypoint: Waypoint = {
        clientId,
        lat: waypointInput.lat,
        lon: waypointInput.lon,
        ele: waypointInput.ele ?? undefined,
        name: truncateToMaxLength(waypointInput.name || '', 255, 'Waypoint name') || undefined,
        description:
          truncateToMaxLength(waypointInput.description || '', 2048, 'Waypoint description') ||
          undefined,
        time: waypointInput.time ?? Date.now(),
        type: waypointInput.type || '',
        _previousState: undefined,
      }

      // Emit event first (quota checks happen here)
      await eventStore.runInSyncBatch(async () => {
        // Promote a drawn-but-unsaved draft so the waypoint's parent tour exists server-side.
        await this._promoteLocalDraftTour(tour)

        await eventStore.addEvent({
          type: EventType.WAYPOINT_CREATED,
          payload: {
            tourClientId: tourId,
            ...waypoint,
          },
        })
      })

      tour.waypoints.push(waypoint)
      return clientId
    },

    async addWorkspacePOI(poiInput: {
      lat: number
      lon: number
      ele?: number | null
      name?: string
      description?: string
      time?: number
      type?: string
    }) {
      throwIfOfflineReadOnly('create collection POI')

      const eventStore = useEventStore()
      const clientId = generateId()
      const poi: WorkspacePOI = {
        clientId,
        workspaceClientId: this.clientId,
        sourceWorkspaceClientId: this.clientId,
        isPinned: isPinnedWorkspaceState(this),
        canEdit: true,
        lat: poiInput.lat,
        lon: poiInput.lon,
        ele: poiInput.ele ?? undefined,
        name: truncateToMaxLength(poiInput.name || '', 255, 'POI name') || undefined,
        description:
          truncateToMaxLength(poiInput.description || '', 2048, 'POI description') || undefined,
        time: poiInput.time,
        type: poiInput.type || '',
        _previousState: undefined,
      }

      const poiPayload = { ...poi }
      delete poiPayload._previousState

      await eventStore.addEvent({
        type: EventType.WORKSPACE_POI_CREATED,
        payload: poiPayload,
      })

      poi._previousState = poiSnapshot(poi)
      this.pois.push(poi)
      return clientId
    },

    async updateWorkspacePOI(clientId: string, updates: Partial<WorkspacePOI>) {
      throwIfOfflineReadOnly('update collection POI')

      const eventStore = useEventStore()
      const poi = this.pois.find((candidate) => candidate.clientId === clientId)
      if (!poi) return
      if (!(await requireItemEditable(poi.canEdit, 'waypoint', poi.createdByName))) return

      const previousState = poi._previousState ?? poiSnapshot(poi)
      poi._previousState = previousState

      const newState = {
        clientId: poi.clientId,
        workspaceClientId: poi.workspaceClientId,
        lat: updates.lat !== undefined ? updates.lat : poi.lat,
        lon: updates.lon !== undefined ? updates.lon : poi.lon,
        ele: updates.ele !== undefined ? updates.ele : poi.ele,
        name:
          updates.name !== undefined
            ? truncateToMaxLength(updates.name, 255, 'POI name')
            : poi.name,
        description:
          updates.description !== undefined
            ? truncateToMaxLength(updates.description, 2048, 'POI description')
            : poi.description,
        time: updates.time !== undefined ? updates.time : poi.time,
        type: updates.type !== undefined ? updates.type : poi.type,
      }

      const changes: Array<{ field: string; value?: string | number | null }> = []
      if (previousState.name !== newState.name) {
        changes.push({ field: 'name', value: newState.name ?? null })
      }
      if (previousState.description !== newState.description) {
        changes.push({ field: 'description', value: newState.description ?? null })
      }
      if (previousState.lat !== newState.lat) {
        changes.push({ field: 'lat', value: newState.lat })
      }
      if (previousState.lon !== newState.lon) {
        changes.push({ field: 'lon', value: newState.lon })
      }
      if (previousState.ele !== newState.ele) {
        changes.push({ field: 'ele', value: newState.ele ?? null })
      }
      if (previousState.time !== newState.time) {
        changes.push({ field: 'time', value: newState.time ?? null })
      }
      if (previousState.type !== newState.type) {
        changes.push({ field: 'type', value: newState.type ?? null })
      }

      if (changes.length > 0) {
        await eventStore.addEvent({
          type: EventType.WORKSPACE_POI_UPDATED,
          payload: {
            clientId,
            changes: changes.map((change) => ({
              field: change.field,
              value: change.value as string | number | boolean | File | null,
            })),
          },
        })
      }

      Object.assign(poi, newState)
      poi._previousState = poiSnapshot(poi)
    },

    async deleteWorkspacePOI(clientId: string) {
      throwIfOfflineReadOnly('delete collection POI')

      const eventStore = useEventStore()
      const uiStore = useUiStore()
      const poiIndex = this.pois.findIndex((poi) => poi.clientId === clientId)
      if (poiIndex === -1) return
      const targetPOI = this.pois[poiIndex]
      if (!(await requireItemEditable(targetPOI.canEdit, 'waypoint', targetPOI.createdByName)))
        return

      const [removedPOI] = this.pois.splice(poiIndex, 1)
      if (uiStore.hoveredWaypointId === `poi_${clientId}`) {
        uiStore.setHoveredWaypoint(null)
      }

      try {
        await eventStore.addEvent({
          type: EventType.WORKSPACE_POI_DELETED,
          payload: { clientId },
        })
      } catch (error) {
        this.pois.splice(poiIndex, 0, removedPOI)
        showError(`Failed to delete POI: ${error instanceof Error ? error.message : String(error)}`)
        throw error
      }
    },

    async removeWorkspacePOIFromCurrentWorkspace(clientId: string) {
      throwIfOfflineReadOnly('remove collection POI')
      if (!this.clientId) return

      const eventStore = useEventStore()
      const poiIndex = this.pois.findIndex((poi) => poi.clientId === clientId)
      if (poiIndex === -1) return

      const poi = this.pois[poiIndex]
      // Unlinking (unlike editing) is curation: the collection owner or the item's own
      // creator may do it — mirrors the backend's validate_workspace_owner_or_creator.
      if (
        !(await requireItemEditable(
          this.isOwner ? true : poi.canEdit,
          'waypoint',
          poi.createdByName,
        ))
      )
        return
      await eventStore.addEvent({
        type: EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE,
        payload: {
          clientId,
          workspaceClientId: this.clientId,
        },
      })
      this.pois.splice(poiIndex, 1)
      if (poi.sourceWorkspaceClientId === this.clientId) {
        poi.isPinned = false
      }
    },

    calculateMapBounds() {
      // Create bounds from all visible tours
      const bounds = new maplibregl.LngLatBounds()
      let hasPoints = false

      this.tours.forEach((tour) => {
        if (!tour.visible) return

        tour.tracks.forEach((track) => {
          const lineString = getBestTrackLineString(track)
          if (lineString) {
            const coordinates = parseLineString(lineString)
            coordinates.forEach(([lon, lat]: [number, number]) => {
              bounds.extend([lon, lat])
              hasPoints = true
            })
          } else {
            // Handle local tracks with segments
            track.segments.forEach((segment: Segment) => {
              segment.points.forEach((point: Waypoint) => {
                bounds.extend([point.lon, point.lat])
                hasPoints = true
              })
            })
          }
        })

        tour.waypoints.forEach((point) => {
          bounds.extend([point.lon, point.lat])
          hasPoints = true
        })
      })

      this.pois.forEach((poi) => {
        bounds.extend([poi.lon, poi.lat])
        hasPoints = true
      })

      return hasPoints ? bounds : null
    },

    calculateTourBounds(tour: Tour) {
      const bounds = new maplibregl.LngLatBounds()
      let hasPoints = false

      tour.tracks.forEach((track) => {
        const lineString = getBestTrackLineString(track)
        if (lineString) {
          const coordinates = parseLineString(lineString)
          coordinates.forEach(([lon, lat]: [number, number]) => {
            bounds.extend([lon, lat])
            hasPoints = true
          })
        } else {
          // Handle local tracks with segments
          track.segments.forEach((segment: Segment) => {
            segment.points.forEach((point: Waypoint) => {
              bounds.extend([point.lon, point.lat])
              hasPoints = true
            })
          })
        }
      })

      tour.waypoints.forEach((point) => {
        bounds.extend([point.lon, point.lat])
        hasPoints = true
      })

      return hasPoints ? bounds : null
    },

    /**
     * Ensure the current collection exists on the server before a direct (non-event-sourced) API
     * call. A freshly-created collection's WORKSPACE_CREATED may still be queued; flush it and
     * wait. Throws WorkspaceNotSyncedError when it cannot be persisted (e.g. offline) so callers
     * can surface an honest message instead of a raw 404.
     * See docs/concepts/sync-ordering-edge-cases.md (Group 2).
     */
    async ensureWorkspacePersistedOnServer() {
      if (this.isPersistedToServer) return

      if (connectivityService.isEffectivelyOnline.value) {
        await syncService.synchronize()
        await syncService.waitForSyncIdle()
      }

      if (!this.isPersistedToServer) {
        throw new WorkspaceNotSyncedError()
      }
    },

    /**
     * Update workspace details
     */
    async updateWorkspace(updates: { name?: string; description?: string; isPublic?: boolean }) {
      const eventStore = useEventStore()

      // Handle isPublic separately using the new API endpoint
      if (updates.isPublic !== undefined) {
        try {
          // Visibility is a direct API call — the server must know the collection first.
          await this.ensureWorkspacePersistedOnServer()

          const response = await apiService.updateWorkspaceVisibility(
            this.clientId,
            updates.isPublic,
          )
          if (response.success) {
            this.isPublic = response.isPublic
            // No persistence to IndexedDB - data stays in memory only
          } else {
            throw new Error(response.message || 'Failed to update workspace visibility')
          }
        } catch (error) {
          showError(
            error instanceof WorkspaceNotSyncedError
              ? error.message
              : `Failed to update workspace visibility: ${error instanceof Error ? error.message : String(error)}`,
          )
          throw error
        }
      }

      // Handle other fields with event sourcing (excluding isPublic)
      const remainingUpdates = { ...updates }
      delete remainingUpdates.isPublic

      if (Object.keys(remainingUpdates).length > 0) {
        const changes: EventChange[] = []

        // Add each field to changes, explicitly excluding 'clientId' and 'isPublic'
        Object.entries(remainingUpdates).forEach(([field, value]) => {
          if (field !== 'clientId' && field !== 'isPublic') {
            changes.push({ field, value })
          }
        })

        if (changes.length > 0) {
          eventStore.addEvent({
            type: EventType.WORKSPACE_UPDATED,
            payload: {
              clientId: this.clientId,
              changes,
            },
          })
        }
      }

      if (updates.name !== undefined)
        this.name = truncateToMaxLength(updates.name, 255, 'Workspace name') || updates.name
      if (updates.description !== undefined)
        this.description =
          truncateToMaxLength(updates.description, 2048, 'Workspace description') ||
          updates.description ||
          null

      await this.saveWorkspaceToDb()
      return this.clientId
    },

    /**
     * Delete the current workspace
     */
    async deleteWorkspace() {
      const eventStore = useEventStore()
      const workspaceIdToDelete = this.clientId

      // Mark workspace as pending deletion
      this._pendingDeletion = true
      await this.saveWorkspaceToDb()

      try {
        // Send the DELETE event
        await eventStore.addEvent({
          type: EventType.WORKSPACE_DELETED,
          payload: {
            clientId: workspaceIdToDelete,
          },
        })

        // After successful event, mark as deleted
        this._pendingDeletion = true
        // No persistence to IndexedDB - data stays in memory only

        // Clear all tours first
        await this.clearAllTours()
        this.pois = []

        // Finally delete from the database
        // No deletion from IndexedDB - data is memory-only

        // Reset local state
        this.clientId = ''
        this.name = ''
        this.description = null
      } catch (error) {
        // In case of error, revert the pending deletion flag
        this._pendingDeletion = false
        // No persistence to IndexedDB - data stays in memory only
        throw error
      }
    },

    /**
     * Mark the current workspace for deletion (soft delete)
     */
    async markWorkspaceForDeletion() {
      try {
        // Soft-delete is a direct API call — the server must know the collection first.
        await this.ensureWorkspacePersistedOnServer()

        // Call the API to mark workspace for deletion
        await apiService.deleteWorkspace(this.clientId)

        // Update local state (deletion fields will be updated via API response during next sync)
        this.markedForDeletion = true
        // Set deletion date to 7 days from now (typical deletion grace period)
        const deletionDate = new Date()
        deletionDate.setDate(deletionDate.getDate() + 7)
        this.deletionScheduledAt = deletionDate.toISOString()

        // No persistence to IndexedDB - data stays in memory only
      } catch (error) {
        showError(
          error instanceof WorkspaceNotSyncedError
            ? error.message
            : 'Failed to mark workspace for deletion',
        )
        throw error
      }
    },

    /**
     * Restore a workspace that was marked for deletion
     */
    async restoreWorkspace() {
      try {
        // Restore is a direct API call — the server must know the collection first (in practice
        // it always does, since only a soft-deleted collection can be restored).
        await this.ensureWorkspacePersistedOnServer()

        // Call the API to restore workspace
        await apiService.restoreWorkspace(this.clientId)

        // Update local state (deletion fields will be updated via API response during next sync)
        this.markedForDeletion = false
        this.deletionScheduledAt = null

        // No persistence to IndexedDB - data stays in memory only
      } catch (error) {
        showError(
          error instanceof WorkspaceNotSyncedError ? error.message : 'Failed to restore workspace',
        )
        throw error
      }
    },

    /**
     * Leave a collection the current user was invited to (self-removal).
     * A shared collection is always on the server, so this is a direct API call.
     * Owners cannot leave — the backend rejects this with a 403 (they delete instead).
     * The caller navigates back to the overview afterwards, where the list reload
     * drops the collection now that the user no longer has access.
     */
    async leaveWorkspace() {
      try {
        await apiService.leaveWorkspace(this.clientId)
      } catch (error) {
        showError('Failed to leave collection')
        throw error
      }
    },

    /**
     * Set or clear the cover photo for this workspace.
     * Uses a direct API call (not event sourcing) — same pattern as updateWorkspaceVisibility.
     *
     * Applies the cover immediately in the UI (optimistic), then waits for the photo to finish
     * syncing to the server if needed before making the PATCH call. This prevents the
     * "Photo not found" error when the user sets a cover on a slow connection.
     */
    async setCoverPhoto(coverPhotoClientId: string | null) {
      const photoStore = usePhotoStore()
      const workspaceId = this.clientId

      // ── 1. Apply local state immediately so the UI reflects the change at once ──
      this.coverPhotoClientId = coverPhotoClientId
      if (coverPhotoClientId) {
        const photo = photoStore.allPhotos.find((p) => p.clientId === coverPhotoClientId)
        if (photo) {
          // Best available local URL: server URL > blob from raw File > thumbnail
          this.coverPhotoUrl =
            photo._binaryDataUrl ||
            (photo.binaryData instanceof File ? URL.createObjectURL(photo.binaryData) : null) ||
            photo._thumbnailUrl ||
            photo._thumbnailData ||
            null
          this.coverPhotoThumbnailUrl =
            photo._thumbnailData || photo._thumbnailUrl || photo._binaryDataUrl || null
        }
      } else {
        this.coverPhotoUrl = null
        this.coverPhotoThumbnailUrl = null
      }

      // Update search catalog immediately so list rows and map popups reflect the new cover
      if (workspaceId) {
        useSearchWorkspaceStore().updateWorkspaceCoverPhoto(
          workspaceId,
          this.coverPhotoThumbnailUrl ?? null,
        )
      }

      // ── 2. Queue the cover assignment — the sync service sends this after any
      //        pending PHOTO_CREATED event for the same photo (queue order guarantees it). ──
      logService.info(LOG_CATEGORIES.SYNC, '[setCoverPhoto] Calling addEvent', {
        workspaceId,
        coverPhotoClientId,
      })
      try {
        await useEventStore().addEvent({
          type: EventType.WORKSPACE_COVER_PHOTO_SET,
          payload: { clientId: workspaceId, coverPhotoClientId },
        })
        logService.info(LOG_CATEGORIES.SYNC, '[setCoverPhoto] addEvent completed successfully', {
          workspaceId,
          coverPhotoClientId,
        })
      } catch (error) {
        logService.error(LOG_CATEGORIES.SYNC, '[setCoverPhoto] addEvent threw an error', { error })
        throw error
      }
    },

    /**
     * Helper method to save current workspace data to IndexedDB
     */
    async saveWorkspaceToDb() {
      if (!this.clientId) {
        showError('Cannot save workspace: no workspace ID')
        return
      }

      // No persistence to IndexedDB - data stays in memory only
      // Workspace data is already in the store state
    },

    /**
     * Process workspace events
     */
    async addWorkspaceFromEvent(workspace: Workspace) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        // Assign workspace data to the store
        this.clientId = workspace.clientId
        this.name = workspace.name
        this.description = workspace.description || null
        this.isPublic = workspace.isPublic || false
        this.markedForDeletion = workspace.markedForDeletion || false
        this.deletionScheduledAt = workspace.deletionScheduledAt || null
        this.isBookmarked = workspace.isBookmarked || false

        // Initialize previous state snapshot for change detection with all fields
        this._previousState = {
          clientId: this.clientId,
          name: this.name,
          description: this.description,
          isPublic: this.isPublic,
          markedForDeletion: this.markedForDeletion,
          deletionScheduledAt: this.deletionScheduledAt,
        }

        // No persistence to IndexedDB - data stays in memory only
        this._createEventPushed = true

        // Auto-show WorkspaceDetailView if workspace is marked for deletion
        // Commented out - detail panel will be opened manually via info button
        // if (this.markedForDeletion) {
        //   const uiStore = useUiStore()
        //   uiStore.showWorkspaceDetailPanel()
        // }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    async updateWorkspaceFromEvent(workspaceId: string, changes: Partial<Workspace>) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        if (this.clientId === workspaceId) {
          // Apply any other changes that might be added in the future
          if (changes.name !== undefined) {
            this.name = truncateToMaxLength(changes.name) || changes.name
          }
          if (changes.description !== undefined) {
            this.description = truncateToMaxLength(changes.description || '', 2048) || null
          }
          // Skip isPublic - handled directly via API endpoint

          // Update previous state snapshot for change detection with all fields
          this._previousState = {
            clientId: this.clientId,
            name: this.name,
            description: this.description,
            isPublic: this.isPublic,
          }

          // Save updated workspace to IndexedDB
          // No persistence to IndexedDB - data stays in memory only
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    async renameWorkspaceFromEvent(workspaceId: string, newName: string) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        if (this.clientId === workspaceId) {
          this.name = truncateToMaxLength(newName) || newName

          // Update previous state snapshot for change detection
          if (this._previousState) {
            this._previousState.name = this.name
          }

          // Save updated workspace to IndexedDB
          // No persistence to IndexedDB - data stays in memory only
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    async deleteWorkspaceFromEvent(workspaceId: string) {
      // Set loading flag to prevent subscription events during sync operations
      const wasLoading = this._isLoading
      this._isLoading = true

      try {
        if (this.clientId === workspaceId) {
          // Delete workspace from IndexedDB
          // No deletion from IndexedDB - data is memory-only

          // Clear workspace data
          this.clientId = ''
          this.name = ''
          this.description = null

          // Clear all tours associated with this workspace
          await this.clearAllTours()
          this.pois = []
        }
      } finally {
        // Restore original loading state
        this._isLoading = wasLoading
      }
    },

    // Override the existing updateWorkspaceName method to use event sourcing
    updateWorkspaceName(name: string) {
      return this.updateWorkspace({ name })
    },

    // Methods needed for map toolbar
    async setAllToursVisibility(visible: boolean) {
      for (const tour of this.tours) {
        tour.visible = visible

        // Also update visibility for all tracks in the tour
        // Note: Visibility changes are intentionally not synced to the server
        for (const track of tour.tracks) {
          track.visible = visible
        }

        persistWorkspaceTourVisibility(this.clientId, this.tours)
        // No persistence to IndexedDB - visibility stays local-only in localStorage
      }
    },

    // Methods needed for track management
    async addTrack(
      tourId: string,
      trackInput: {
        clientId?: string
        name: string
        segments: Segment[]
        color?: string
        routingProfile?: string
        type?: string
      },
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return null
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null

      const clientId = trackInput.clientId ?? generateId()
      const color = trackInput.color ?? TrackColorService.getNewTrackColorForTour(tour.tracks)
      const segments = ensureSegmentClientIds(trackInput.segments)

      const track: Track = {
        clientId,
        name: truncateToMaxLength(trackInput.name, 255, 'Track name'),
        segments,
        visible: true,
        color,
        routingProfile: trackInput.routingProfile,
        type: trackInput.type ?? tour.type ?? undefined,
        _previousState: undefined,
      }

      // Emit event
      await eventStore.addEvent({
        type: EventType.TRACK_CREATED,
        payload: {
          tour_client_id: tourId,
          ...track,
        },
      })

      tour.tracks.push(track)
      return clientId
    },

    async commitTrackRouteDraft(input: TrackRouteDraftCommitInput) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === input.tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const trackClientId = input.trackClientId
      const finalSegments = ensureSegmentClientIds(clonePlain(input.draftTrack.segments))
      const finalRoutePoints = clonePlain(input.draftRoutePoints).map((routePoint) => ({
        ...routePoint,
        trackClientId,
      }))

      if (input.mode === 'drawing') {
        const existingTrackIndex = tour.tracks.findIndex(
          (track) => track.clientId === trackClientId,
        )
        const seededRoutePoints = finalRoutePoints.map((routePoint, index) => ({
          ...routePoint,
          order: getSparseTrackSeedOrders(finalRoutePoints.length)[index] ?? routePoint.order,
        }))

        const track: Track = {
          ...clonePlain(input.draftTrack),
          clientId: trackClientId,
          name: truncateToMaxLength(input.draftTrack.name ?? 'New Track', 255, 'Track name'),
          color: input.draftTrack.color ?? TrackColorService.getNewTrackColorForTour(tour.tracks),
          visible: input.draftTrack.visible ?? true,
          type: input.draftTrack.type ?? tour.type ?? undefined,
          segments: finalSegments,
          pointsLineReduced5m:
            input.draftTrack.pointsLineReduced5m ?? buildLineStringFromSegments(finalSegments),
          _previousState: undefined,
        }

        await eventStore.runInSyncBatch(async () => {
          await this._promoteLocalDraftTour(tour)

          await eventStore.addEvent({
            type: EventType.TRACK_CREATED,
            payload: {
              tour_client_id: input.tourId,
              ...track,
            },
          })

          for (const routePoint of seededRoutePoints) {
            await eventStore.addEvent({
              type: EventType.ROUTE_POINT_CREATED,
              payload: routePoint,
            })
          }
        })

        if (existingTrackIndex >= 0) {
          tour.tracks.splice(existingTrackIndex, 1, track)
        } else {
          tour.tracks.push(track)
        }
        replaceTrackRoutePoints(tour, trackClientId, seededRoutePoints)
        refreshTrackMetrics(track)
        refreshTourTrackStats(tour)
        return
      }

      const track = tour.tracks.find((candidate) => candidate.clientId === trackClientId)
      if (!track) return
      const draftRoutingProfile = input.draftTrack.routingProfile ?? null
      const baselineRoutingProfile = input.baselineTrack?.routingProfile ?? null

      const baselineSegments = clonePlain(input.baselineTrack?.segments ?? track.segments)
      const baselineRoutePoints = clonePlain(
        input.baselineRoutePoints.length > 0
          ? input.baselineRoutePoints
          : tour.routePoints.filter(
              (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
            ),
      )

      const baselineSegmentsById = new Map(
        baselineSegments
          .filter((segment) => !!segment.clientId)
          .map((segment, index) => [segment.clientId!, { segment, index }] as const),
      )
      const finalSegmentIds = new Set(
        finalSegments.map((segment) => segment.clientId).filter((id): id is string => !!id),
      )
      const deletedSegmentIds = baselineSegments
        .map((segment) => segment.clientId)
        .filter((clientId): clientId is string => !!clientId && !finalSegmentIds.has(clientId))
      const createdSegments = finalSegments
        .map((segment, order) => ({ segment, order }))
        .filter(({ segment }) => !segment.clientId || !baselineSegmentsById.has(segment.clientId))

      const survivorOriginalOrder = new Map<string, number>()
      baselineSegmentsById.forEach(({ index }, clientId) => {
        survivorOriginalOrder.set(clientId, index)
      })

      const shiftedSegmentUpdates: Array<{
        segment: Segment
        order: number
        originalOrder: number
        contentChanged: boolean
      }> = []
      const sameOrderSegmentUpdates: Array<{ segment: Segment; order: number }> = []

      finalSegments.forEach((segment, order) => {
        if (!segment.clientId) return
        const baseline = baselineSegmentsById.get(segment.clientId)
        if (!baseline) return

        const contentChanged = !sameSegment(segment, baseline.segment)
        if (baseline.index !== order) {
          shiftedSegmentUpdates.push({
            segment,
            order,
            originalOrder: baseline.index,
            contentChanged,
          })
        } else if (contentChanged) {
          sameOrderSegmentUpdates.push({ segment, order })
        }
      })

      const shiftedSegmentOrder = sortSegmentOrderShiftsForEmission(
        shiftedSegmentUpdates.map(({ segment, order }) => ({
          clientId: segment.clientId!,
          newOrder: order,
        })),
        survivorOriginalOrder,
      )
      const shiftedSegmentById = new Map(
        shiftedSegmentUpdates.map((update) => [update.segment.clientId!, update] as const),
      )

      const baselineRoutePointsById = new Map(
        baselineRoutePoints.map((routePoint) => [routePoint.clientId, routePoint] as const),
      )
      const finalRoutePointIds = new Set(finalRoutePoints.map((routePoint) => routePoint.clientId))
      const deletedRoutePointIds = baselineRoutePoints
        .filter((routePoint) => !finalRoutePointIds.has(routePoint.clientId))
        .map((routePoint) => routePoint.clientId)
      const createdRoutePoints = finalRoutePoints.filter(
        (routePoint) => !baselineRoutePointsById.has(routePoint.clientId),
      )
      const existingRoutePointUpdates = finalRoutePoints
        .map((routePoint) => {
          const baseline = baselineRoutePointsById.get(routePoint.clientId)
          if (!baseline) return null

          const changes: EventChange[] = []
          if (baseline.lat !== routePoint.lat || baseline.lon !== routePoint.lon) {
            changes.push({ field: 'location', value: routePointLocation(routePoint) })
          }
          if (baseline.order !== routePoint.order) {
            changes.push({ field: 'order', value: routePoint.order })
          }
          if ((baseline.name ?? null) !== (routePoint.name ?? null)) {
            changes.push({ field: 'name', value: routePoint.name ?? null })
          }
          if ((baseline.description ?? null) !== (routePoint.description ?? null)) {
            changes.push({ field: 'description', value: routePoint.description ?? null })
          }
          return changes.length > 0 ? { routePoint, baseline, changes } : null
        })
        .filter(
          (
            update,
          ): update is {
            routePoint: RoutePoint
            baseline: RoutePoint
            changes: EventChange[]
          } => update !== null,
        )
      const reorderedRoutePointUpdates = existingRoutePointUpdates.filter(
        ({ baseline, routePoint }) => baseline.order !== routePoint.order,
      )
      const nonOrderRoutePointUpdates = existingRoutePointUpdates.filter(
        ({ baseline, routePoint }) => baseline.order === routePoint.order,
      )

      await eventStore.runInSyncBatch(async () => {
        if (baselineRoutingProfile !== draftRoutingProfile) {
          await eventStore.addEvent({
            type: EventType.TRACK_UPDATED,
            payload: {
              clientId: trackClientId,
              changes: [{ field: 'routingProfile', value: draftRoutingProfile }],
            },
          })
        }

        for (const clientId of deletedSegmentIds) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_DELETED,
            payload: { clientId },
          })
        }

        for (const { clientId, newOrder } of shiftedSegmentOrder) {
          const update = shiftedSegmentById.get(clientId)
          if (!update) continue
          await eventStore.addEvent({
            type: EventType.SEGMENT_UPDATED,
            payload: {
              ...(update.contentChanged ? update.segment : {}),
              clientId,
              trackClientId,
              order: newOrder,
              ...(update.contentChanged ? { points: clonePlain(update.segment.points) } : {}),
            },
          })
        }

        for (const { segment, order } of sameOrderSegmentUpdates) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_UPDATED,
            payload: {
              ...segment,
              clientId: segment.clientId!,
              trackClientId,
              order,
              points: clonePlain(segment.points),
            },
          })
        }

        for (const { segment, order } of createdSegments) {
          const segmentClientId = segment.clientId ?? generateId()
          segment.clientId = segmentClientId
          await eventStore.addEvent({
            type: EventType.SEGMENT_CREATED,
            payload: {
              ...segment,
              trackClientId,
              clientId: segmentClientId,
              order,
              points: clonePlain(segment.points),
            },
          })
        }

        for (const clientId of deletedRoutePointIds) {
          await eventStore.queueRoutePointDelete(clientId)
        }

        for (const routePoint of createdRoutePoints) {
          await eventStore.addEvent({
            type: EventType.ROUTE_POINT_CREATED,
            payload: routePoint,
          })
        }

        await emitRoutePointOrderChanges(
          (clientId, changes) => eventStore.queueRoutePointUpdate(clientId, changes),
          reorderedRoutePointUpdates.map(({ routePoint, baseline, changes }) => ({
            clientId: routePoint.clientId,
            previousOrder: baseline.order,
            nextOrder: routePoint.order,
            finalChanges: changes,
          })),
          baselineRoutePoints.map((routePoint) => routePoint.order),
          finalRoutePoints.map((routePoint) => routePoint.order),
        )

        for (const { routePoint, changes } of nonOrderRoutePointUpdates) {
          await eventStore.queueRoutePointUpdate(routePoint.clientId, changes)
        }
      })

      track.segments = finalSegments
      track.routingProfile = input.draftTrack.routingProfile
      track.pointsLineReduced5m =
        input.draftTrack.pointsLineReduced5m ?? buildLineStringFromSegments(finalSegments)
      refreshTrackMetrics(track)
      refreshTourTrackStats(tour)
      replaceTrackRoutePoints(tour, trackClientId, finalRoutePoints)
    },

    async updateTrack(tourId: string, trackIndex: number, updates: Partial<Track>) {
      const eventStore = useEventStore()
      const tour = this.tours.find((tour) => tour.clientId === tourId)

      if (tour && trackIndex >= 0 && trackIndex < tour.tracks.length) {
        // Tracks follow the parent tour's creator-only edit rule (validate_tour_access).
        if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

        const track = tour.tracks[trackIndex]

        // Get current state for all track fields (only flat properties)
        const currentState = {
          clientId: track.clientId,
          name: track.name,
          color: track.color,
          visible: track.visible,
          type: track.type,
        }

        // Apply updates to current state to get new state
        const newState = {
          clientId: track.clientId,
          name:
            updates.name !== undefined
              ? truncateToMaxLength(updates.name, 255, 'Track name')
              : track.name,
          color: updates.color !== undefined ? updates.color : track.color,
          visible: track.visible,
          type: updates.type !== undefined ? updates.type : track.type,
        }

        // Initialize previous state if it doesn't exist
        if (!track._previousState) {
          track._previousState = { ...currentState }
        }

        // Check for changes in all fields
        const changes: { field: string; value: string | undefined }[] = []

        if (track._previousState.name !== newState.name) {
          changes.push({ field: 'name', value: newState.name })
        }
        if (track._previousState.color !== newState.color) {
          changes.push({ field: 'color', value: newState.color })
        }
        if (track._previousState.type !== newState.type) {
          changes.push({ field: 'type', value: newState.type })
        }

        // Create event if we have changes
        if (changes.length > 0) {
          const eventChanges: EventChange[] = changes.map((change) => ({
            field: change.field,
            value: change.value as string | number | boolean | File | null,
          }))

          await eventStore.addEvent({
            type: EventType.TRACK_UPDATED,
            payload: {
              clientId: track.clientId,
              changes: eventChanges,
            },
          })

          // Update snapshot after creating the event
          track._previousState = { ...newState }
        }

        Object.assign(track, updates)
        // No persistence to IndexedDB - data stays in memory only
      }
    },

    async setTrackVisibility(tourId: string, trackIndex: number, visible: boolean) {
      const tour = this.tours.find((tour) => tour.clientId === tourId)

      if (tour && trackIndex >= 0 && trackIndex < tour.tracks.length) {
        materializeTrackVisibility(tour)
        const track = tour.tracks[trackIndex]
        track.visible = visible
        updateTourVisibilityFromTracks(tour)
        persistWorkspaceTourVisibility(this.clientId, this.tours)
        // No persistence to IndexedDB - data stays in memory only
      }
    },

    async toggleAllTracksVisibility(tourId: string, visible: boolean) {
      const tour = this.tours.find((tour) => tour.clientId === tourId)
      if (tour) {
        tour.tracks.forEach((track) => {
          track.visible = visible
        })
        tour.visible = visible
        persistWorkspaceTourVisibility(this.clientId, this.tours)
      }
    },

    async ensureTourDetailLoaded(tourId: string) {
      const existingTour = this.tours.find((tour) => tour.clientId === tourId) || null
      if (!existingTour || existingTour._detailsLoaded) {
        return existingTour
      }

      const pendingLoad = pendingTourDetailLoads.get(tourId)
      if (pendingLoad) {
        return await pendingLoad
      }

      this._loadingTourDetailId = tourId

      const loadPromise = (async () => {
        try {
          const fullTour = await apiService.getTourDetail(tourId)
          const currentTour = this.tours.find((tour) => tour.clientId === tourId) || null
          if (!currentTour) {
            return null
          }

          // The standalone tour-detail endpoint has no collection-membership context, so it
          // returns null collection_name/custom_name. Strip those keys before merging so they
          // can't clobber the collection-scoped name resolved from the collection load —
          // normalizeApiTour then falls back to the existing tour's customName/collectionName.
          const detailPayload = { ...(fullTour as unknown as Record<string, unknown>) }
          delete detailPayload.collectionName
          delete detailPayload.collection_name
          delete detailPayload.customName
          delete detailPayload.custom_name

          const hydratedTour = normalizeApiTour(detailPayload, {
            existingTour: currentTour,
            detailsLoaded: true,
          })
          applyPersistedWorkspaceTourVisibility(this.clientId, [hydratedTour])
          const tourIndex = this.tours.findIndex((tour) => tour.clientId === tourId)
          if (tourIndex === -1) {
            return null
          }

          this.tours.splice(tourIndex, 1, hydratedTour)
          return hydratedTour
        } catch (error) {
          logService.error(LOG_CATEGORIES.API, 'Failed to hydrate workspace tour detail', {
            tourId,
            error,
          })
          throw error
        } finally {
          pendingTourDetailLoads.delete(tourId)
          if (this._loadingTourDetailId === tourId) {
            this._loadingTourDetailId = null
          }
        }
      })()

      pendingTourDetailLoads.set(tourId, loadPromise)
      return await loadPromise
    },

    /**
     * Select a tour to display its details
     * @param tourId The tour ID to select, or null to clear selection
     */
    selectTour(tourId: string | null) {
      this._selectedTourId = tourId
      this._hoveredTourId = tourId
      this._activelyHoveredTourId = null
      if (tourId) {
        void this.ensureTourDetailLoaded(tourId)
      } else {
        this._loadingTourDetailId = null
      }
    },

    /**
     * Explicitly set the selected tour ID
     * @param tourId The tour ID to select, or null to clear selection
     */
    setSelectedTourId(tourId: string | null) {
      this._selectedTourId = tourId
      this._hoveredTourId = tourId
      this._activelyHoveredTourId = null
      if (tourId) {
        void this.ensureTourDetailLoaded(tourId)
      } else {
        this._loadingTourDetailId = null
      }
    },

    setHoveredTourId(tourId: string | null) {
      this._hoveredTourId = tourId
    },

    setActivelyHoveredTourId(tourId: string | null) {
      this._activelyHoveredTourId = tourId
    },

    setTourWaypointVisibility(tourId: string, visible: boolean) {
      const tourIndex = this.tours.findIndex((tour) => tour.clientId === tourId)
      if (tourIndex !== -1) {
        this.tours[tourIndex]._waypointsVisible = visible
      }
    },

    toggleTourWaypointVisibility(tourId: string) {
      const tour = this.tours.find((tour) => tour.clientId === tourId)
      if (tour) {
        const currentVisibility = tour._waypointsVisible ?? true
        this.setTourWaypointVisibility(tourId, !currentVisibility)
      }
    },

    async deleteTrack(tourId: string, trackIndex: number) {
      const eventStore = useEventStore()
      const tour = this.tours.find((tour) => tour.clientId === tourId)

      if (tour && trackIndex >= 0 && trackIndex < tour.tracks.length) {
        if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

        const track = tour.tracks[trackIndex]

        // Mark track as pending deletion
        track._pendingDeletion = true
        // No persistence to IndexedDB - data stays in memory only

        try {
          // Send the DELETE event
          await eventStore.addEvent({
            type: EventType.TRACK_DELETED,
            payload: {
              clientId: track.clientId,
            },
          })

          // After successful event, mark as pending deletion
          track._pendingDeletion = true
          // No persistence to IndexedDB - data stays in memory only

          // Now remove from the array
          tour.tracks.splice(trackIndex, 1)

          // Remove any route points that belonged to this track.
          // Backend cascades RoutePoint on Track delete (on_delete=CASCADE),
          // but the frontend keeps route points in tour.routePoints tagged by
          // trackClientId, so we must also drop them locally or the map will
          // show stale markers for a track that no longer exists.
          if (tour.routePoints && tour.routePoints.length) {
            tour.routePoints = tour.routePoints.filter((rp) => rp.trackClientId !== track.clientId)
          }
          useUiStore().handleTrackDeleted(tourId, track.clientId)
          // No persistence to IndexedDB - data stays in memory only
        } catch (error) {
          // In case of error, revert the pending deletion flag
          track._pendingDeletion = false
          // No persistence to IndexedDB - data stays in memory only
          throw error
        }
      }
    },

    async addSegmentToTrack(
      tourId: string,
      trackClientId: string,
      segment: Segment,
      order: number,
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const track = tour.tracks.find((t) => t.clientId === trackClientId)
      if (!track) return

      // Insert locally at correct position
      const insertIdx = track.segments.findIndex((_, i) => i >= order)
      if (insertIdx === -1) {
        track.segments.push(segment)
      } else {
        track.segments.splice(insertIdx, 0, segment)
      }

      await eventStore.addEvent({
        type: EventType.SEGMENT_CREATED,
        payload: {
          ...segment,
          trackClientId,
          clientId: segment.clientId!,
          order,
          points: segment.points,
        },
      })
    },

    async deleteSegmentFromTrack(tourId: string, trackClientId: string, segmentClientId: string) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const track = tour.tracks.find((t) => t.clientId === trackClientId)
      if (!track) return

      const idx = track.segments.findIndex((s) => s.clientId === segmentClientId)
      if (idx === -1) return

      track.segments.splice(idx, 1)

      await eventStore.addEvent({
        type: EventType.SEGMENT_DELETED,
        payload: {
          clientId: segmentClientId,
        },
      })
    },

    /**
     * Replace segments in a track: delete old segments and insert new ones.
     * Used when route points are moved or deleted.
     */
    async replaceSegments(
      tourId: string,
      trackClientId: string,
      deletedIndices: number[],
      newSegments: { segment: Segment; order: number }[],
      options?: { skipLineStringRebuild?: boolean },
    ) {
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const track = tour.tracks.find((t) => t.clientId === trackClientId)
      if (!track) return

      const validDeletedIndices = deletedIndices.filter((i) => i >= 0 && i < track.segments.length)

      // Detect 1:1 in-place replacement: each deleted index maps to a new
      // segment at the same order. In that case we emit SEGMENT_UPDATED and
      // preserve segment identity instead of DELETE + CREATE.
      const sortedDeleted = [...validDeletedIndices].sort((a, b) => a - b)
      const sortedNew = [...newSegments].sort((a, b) => a.order - b.order)
      const isInPlace =
        sortedDeleted.length > 0 &&
        sortedDeleted.length === sortedNew.length &&
        sortedDeleted.every((idx, i) => idx === sortedNew[i].order)

      const eventStore = useEventStore()

      if (isInPlace) {
        // Reuse old segment clientIds on the new segments (identity preserved)
        const updates: Array<
          {
            clientId: string
            trackClientId: string
            order: number
            points: Segment['points']
          } & Partial<Omit<Segment, 'clientId' | 'points'>>
        > = []
        for (let i = 0; i < sortedDeleted.length; i++) {
          const oldSeg = track.segments[sortedDeleted[i]]
          const preservedClientId = oldSeg.clientId!
          const newSeg = sortedNew[i].segment
          newSeg.clientId = preservedClientId
          track.segments[sortedDeleted[i]] = newSeg
          updates.push({
            ...newSeg,
            clientId: preservedClientId,
            trackClientId,
            order: sortedNew[i].order,
            points: JSON.parse(JSON.stringify(newSeg.points)),
          })
        }

        if (!options?.skipLineStringRebuild) {
          track.pointsLineReduced5m = buildLineStringFromSegments(track.segments)
        }
        refreshTrackMetrics(track)
        refreshTourTrackStats(tour)

        await eventStore.runInSyncBatch(async () => {
          for (const update of updates) {
            await eventStore.addEvent({
              type: EventType.SEGMENT_UPDATED,
              payload: update,
            })
          }
        })
        return
      }

      // Fallback: DELETE + CREATE (segment count changes)
      const toDelete = [...validDeletedIndices].sort((a, b) => b - a)

      const deletedClientIds: string[] = []
      for (const idx of toDelete) {
        const seg = track.segments[idx]
        if (seg.clientId) deletedClientIds.push(seg.clientId)
      }

      // Capture the ORIGINAL array position of every surviving segment
      // (i.e. every existing segment that is NOT being deleted). After the
      // splice operations below, these array positions will change, and the
      // backend `order` field for each surviving segment must be shifted to
      // match — otherwise the new segments inserted into the middle of the
      // array collide with existing `order` values on the server, producing
      // duplicate orders and a non-deterministic sequence on reload.
      const toDeleteSet = new Set(toDelete)
      const survivorOriginalOrder = new Map<string, number>()
      track.segments.forEach((seg, i) => {
        if (seg.clientId && !toDeleteSet.has(i)) {
          survivorOriginalOrder.set(seg.clientId, i)
        }
      })

      for (const idx of toDelete) {
        track.segments.splice(idx, 1)
      }

      for (const { segment, order } of sortedNew) {
        const insertAt = Math.min(order, track.segments.length)
        track.segments.splice(insertAt, 0, segment)
      }

      // Note: pointsLineReduced5m is NOT rebuilt here when skipLineStringRebuild
      // is set, because existing segments loaded from the server may lack point
      // data. Callers must merge coordinates themselves in that case.
      if (!options?.skipLineStringRebuild) {
        track.pointsLineReduced5m = buildLineStringFromSegments(track.segments)
      }
      refreshTrackMetrics(track)
      refreshTourTrackStats(tour)

      // Detect surviving segments whose array index changed and need their
      // backend `order` aligned to the new index (array index == order).
      const createdClientIds = new Set(
        sortedNew.map((n) => n.segment.clientId).filter((id): id is string => !!id),
      )
      const orderShifts: { clientId: string; newOrder: number }[] = []
      track.segments.forEach((seg, newIdx) => {
        if (!seg.clientId) return
        if (createdClientIds.has(seg.clientId)) return
        const origIdx = survivorOriginalOrder.get(seg.clientId)
        if (origIdx !== undefined && origIdx !== newIdx) {
          orderShifts.push({ clientId: seg.clientId, newOrder: newIdx })
        }
      })
      const orderedOrderShifts = sortSegmentOrderShiftsForEmission(
        orderShifts,
        survivorOriginalOrder,
      )

      // Emit events: deletes first, then order shifts (to vacate collision
      // slots), then creates.
      await eventStore.runInSyncBatch(async () => {
        for (const clientId of deletedClientIds) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_DELETED,
            payload: { clientId },
          })
        }
        for (const { clientId, newOrder } of orderedOrderShifts) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_UPDATED,
            payload: {
              clientId,
              trackClientId,
              order: newOrder,
            },
          })
        }
        for (const { segment, order } of sortedNew) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_CREATED,
            payload: {
              ...segment,
              trackClientId,
              clientId: segment.clientId!,
              order,
              points: JSON.parse(JSON.stringify(segment.points)),
            },
          })
        }
      })
    },

    async replaceAllSegments(
      tourId: string,
      trackClientId: string,
      oldSegmentClientIds: string[],
      newSegments: Segment[],
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const track = tour.tracks.find((t) => t.clientId === trackClientId)
      if (!track) return

      await eventStore.runInSyncBatch(async () => {
        // Delete old segments on backend
        for (const clientId of oldSegmentClientIds) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_DELETED,
            payload: { clientId },
          })
        }

        // Create new segments on backend
        for (let i = 0; i < newSegments.length; i++) {
          const segment = newSegments[i]
          await eventStore.addEvent({
            type: EventType.SEGMENT_CREATED,
            payload: {
              ...segment,
              trackClientId,
              clientId: segment.clientId!,
              order: i,
              points: JSON.parse(JSON.stringify(segment.points)),
            },
          })
        }
      })

      // Update local state
      track.segments = newSegments
      track.pointsLineReduced5m = buildLineStringFromSegments(newSegments)
      refreshTrackMetrics(track)
      refreshTourTrackStats(tour)
    },

    async updateRoutePointPosition(
      tourId: string,
      rpIndex: number,
      newLat: number,
      newLon: number,
    ) {
      const eventStore = useEventStore()
      const tour = this.tours.find((t) => t.clientId === tourId)
      if (!tour) return
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

      const rp = tour.routePoints[rpIndex]
      if (!rp) return

      // Update locally
      rp.lat = newLat
      rp.lon = newLon

      // Emit event with EWKT location
      await eventStore.queueRoutePointUpdate(rp.clientId, [
        {
          field: 'location',
          value: `SRID=4326;POINT(${newLon} ${newLat})`,
        },
      ])
    },

    /**
     * Update the previous state snapshot after successful sync
     * This prevents duplicate events from being created for already synced changes
     */
    updateSnapshotAfterSync() {
      // Update snapshot with flat workspace fields including clientId
      this._previousState = {
        clientId: this.clientId,
        name: this.name,
        description: this.description,
        isPublic: this.isPublic,
      }
    },

    /**
     * Update tour snapshots after successful sync
     * This prevents duplicate events from being created for already synced tour changes
     */
    updateTourSnapshotsAfterSync() {
      for (const tour of this.tours) {
        tour._previousState = {
          clientId: tour.clientId,
          name: tour.name,
          description: tour.description,
          visible: tour.visible,
          active: tour.active,
          expanded: tour.expanded,
          order: tour.order,
          distance: tour.distance,
          time_moving: tour.time_moving,
          time_elapsed: tour.time_elapsed,
        }
      }
    },

    /**
     * Update track snapshots after successful sync
     * This prevents duplicate events from being created for already synced track changes
     */
    updateTrackSnapshotsAfterSync() {
      for (const tour of this.tours) {
        for (const track of tour.tracks) {
          track._previousState = {
            clientId: track.clientId,
            name: track.name,
            color: track.color,
            visible: track.visible,
            type: track.type,
          }
        }
      }
    },

    /**
     * Update waypoint snapshots after successful sync
     * This prevents duplicate events from being created for already synced waypoint changes
     */
    updateWaypointSnapshotsAfterSync() {
      for (const tour of this.tours) {
        for (const waypoint of tour.waypoints) {
          waypoint._previousState = {
            clientId: waypoint.clientId,
            name: waypoint.name,
            description: waypoint.description,
            lat: waypoint.lat,
            lon: waypoint.lon,
            ele: waypoint.ele,
            time: waypoint.time,
            type: waypoint.type,
          }
        }
      }
    },

    updateWorkspacePOISnapshotsAfterSync() {
      for (const poi of this.pois) {
        poi._previousState = poiSnapshot(poi)
      }
    },

    /**
     * Ensure all tours have order values assigned
     * This is needed for existing tours that were created before the order field was added
     */
    async ensureToursHaveOrder() {
      let hasChanges = false

      for (let i = 0; i < this.tours.length; i++) {
        const tour = this.tours[i]
        if (tour.order === undefined || tour.order === null) {
          tour.order = i
          hasChanges = true
          // No persistence to IndexedDB - data stays in memory only
        }
      }

      // If we made changes, sort the tours by order
      if (hasChanges) {
        this.tours.sort((a, b) => (a.order || 0) - (b.order || 0))
      }
    },

    /**
     * Get the next order value for a new tour
     */
    getNextTourOrder(): number {
      if (this.tours.length === 0) return 0
      const maxOrder = Math.max(...this.tours.map((tour) => tour.order || 0))
      return maxOrder + 1
    },

    /**
     * Reorder tours by moving a tour from one position to another
     * @param fromIndex The current index of the tour to move
     * @param toIndex The target index to move the tour to
     */
    async reorderTours(fromIndex: number, toIndex: number) {
      const eventStore = useEventStore()

      // Get the current sorted tours first
      const sortedTours = this.tours.slice().sort((a, b) => (a.order || 0) - (b.order || 0))

      // Validate indices against sorted tours
      if (
        fromIndex < 0 ||
        fromIndex >= sortedTours.length ||
        toIndex < 0 ||
        toIndex >= sortedTours.length ||
        fromIndex === toIndex
      ) {
        return
      }

      const affectedTours = sortedTours.slice(
        Math.min(fromIndex, toIndex),
        Math.max(fromIndex, toIndex) + 1,
      )
      for (const tour of affectedTours) {
        if (
          !(await requireItemEditable(
            tour.canEdit,
            tour.type ? 'activity' : 'route',
            tour.createdByName,
          ))
        )
          return
      }

      // Move the tour
      const [movedTour] = sortedTours.splice(fromIndex, 1)
      sortedTours.splice(toIndex, 0, movedTour)

      // Update all order values in a single sync batch, promoting any drawn-but-unsaved draft
      // tour before its order event so its TOUR_CREATED is queued first.
      await eventStore.runInSyncBatch(async () => {
        for (let i = 0; i < sortedTours.length; i++) {
          const tour = sortedTours[i]
          const newOrder = i

          if (tour.order !== newOrder) {
            tour.order = newOrder

            // No persistence to IndexedDB - data stays in memory only

            await this._promoteLocalDraftTour(tour)

            await eventStore.addEvent({
              type: EventType.TOUR_UPDATED,
              payload: {
                clientId: tour.clientId,
                changes: [{ field: 'order', value: newOrder }],
              },
            })
          }
        }
      })
    },

    /**
     * Move a tour to a specific order position
     * @param tourId The ID of the tour to move
     * @param newOrder The new order position (0-based)
     */
    async moveTourToOrder(tourId: string, newOrder: number) {
      const currentIndex = this.tours.findIndex((tour) => tour.clientId === tourId)
      if (currentIndex === -1) return

      // Convert to sorted index
      const sortedTours = this.tours.slice().sort((a, b) => (a.order || 0) - (b.order || 0))
      const sortedIndex = sortedTours.findIndex((tour) => tour.clientId === tourId)

      if (sortedIndex !== -1) {
        await this.reorderTours(sortedIndex, newOrder)
      }
    },

    /**
     * Initialize tour order values for tours that don't have them yet
     * This handles existing data that was created before the order field was added
     */
    initializeTourOrders() {
      let maxOrder = -1
      const toursWithoutOrder: Tour[] = []

      // First pass: find existing max order and identify tours without order
      for (const tour of this.tours) {
        if (tour.order !== undefined && tour.order !== null) {
          maxOrder = Math.max(maxOrder, tour.order)
        } else {
          toursWithoutOrder.push(tour)
        }
      }

      // Second pass: assign order values to tours that don't have them
      for (const tour of toursWithoutOrder) {
        maxOrder++
        tour.order = maxOrder
      }
    },

    /**
     * Clear workspace to a "no collection" state for the overview
     * This removes all workspace data without creating a new default workspace
     */
    clearToOverviewState() {
      // Clear all tours
      this.tours = []
      this.pois = []

      // Clear workspace metadata but keep basic structure
      this.clientId = ''
      this.name = ''
      this.description = null
      this.isPublic = false
      this.systemKey = null
      this.isSystem = false
      this.markedForDeletion = false
      this.deletionScheduledAt = null

      // Clear selection and internal state
      this._selectedTourId = null
      this._loadingTourDetailId = null
      this._lastAddedTourId = null
      this._error = null
      this._isLoading = false
      this._createEventPushed = false
      this._previousState = undefined

      // Clear permissions
      this.userRole = null
      this.canEdit = null
      this.canManage = null
      this.canView = null
      this.isBookmarked = false

      // Clear cover photo
      this.coverPhotoClientId = null
      this.coverPhotoThumbnailUrl = null
      this.coverPhotoUrl = null
    },
  },
})
